import { MobXProviderContext } from "mobx-react";
import { Button } from "primereact/button";
import { confirmDialog } from "primereact/confirmdialog";
import { Calendar } from "primereact/calendar";
import { Checkbox } from "primereact/checkbox";
import { AutoComplete } from "primereact/autocomplete";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { InputNumber } from "primereact/inputnumber";
import { InputTextarea } from "primereact/inputtextarea";
import { MultiSelect } from "primereact/multiselect";
import { ProgressSpinner } from "primereact/progressspinner";
import { SelectButton } from "primereact/selectbutton";
import { Tag } from "primereact/tag";
import { TabPanel, TabView } from "primereact/tabview";
import { Toast } from "primereact/toast";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  appConfirmDialogClasses,
  appInnerCardStyle,
  appMutedTextStyle,
  appNeutralPanelStyle,
  renderConfirmMessage,
  renderDialogFooter,
  renderSurfaceHeader,
} from "../../../components/general/uiSurfaceStyles";
import { AI_MESSAGE_FOR_COMPLIANCE, getErrorTitle } from "../../../constants/constants";
import { ROLE_TYPE_PROVIDER } from "../../../constants/RegisterConstants";
import { RouterAddress } from "../../../constants/RouterAddress";
import { useResponsive } from "../../../responsive/ResponsiveProvider";
import Unauthorized from "../../../shared/pages/Unauthorized";
import { openUrlInNewTab } from "../../../utils/securityUtils";

const supportLevelOptions = [
  "Independent",
  "Supervision",
  "Limited Assistance",
  "Extensive Assistance",
  "Total Assistance",
  "Needs Tracking",
].map((value) => ({ label: value, value }));
const supportLevelParameterOptions = supportLevelOptions.map((option) => option.value);
const adlSupportLevelOptionKeys = new Set(supportLevelParameterOptions.map((option) => String(option).trim().toLowerCase()));
const isDuplicateAdlSupportParameter = (parameter: any) => {
  const label = String(parameter?.label || parameter?.parameterId || "").trim().toLowerCase();
  const options = Array.isArray(parameter?.options)
    ? parameter.options.map((option: any) => String(option || "").trim().toLowerCase()).filter(Boolean)
    : [];
  return ["assistance", "support", "support level", "support required"].includes(label)
    && options.length === supportLevelParameterOptions.length
    && new Set(options).size === adlSupportLevelOptionKeys.size
    && options.every((option: string) => adlSupportLevelOptionKeys.has(option));
};
const getAdlChartingParametersForCharting = (parameters: any[]) => (
  (Array.isArray(parameters) ? parameters : []).filter((parameter: any) => !isDuplicateAdlSupportParameter(parameter))
);

const defaultStatusOptions = [
  "Done",
  "Partial Assist",
  "Full Assist",
  "Refused",
  "Not Done",
  "Observed",
].map((value) => ({ label: value, value }));

const adlChartDialogStyle: React.CSSProperties = { width: "min(56rem, 96vw)" };

const adlItemExamples = [
  { label: "Breakfast", chartingParameters: [{ parameterId: "amount", label: "Amount eaten", type: "select", options: ["0%", "25%", "50%", "75%", "100%"] }] },
  { label: "Lunch", chartingParameters: [{ parameterId: "amount", label: "Amount eaten", type: "select", options: ["0%", "25%", "50%", "75%", "100%"] }] },
  { label: "Dinner", chartingParameters: [{ parameterId: "amount", label: "Amount eaten", type: "select", options: ["0%", "25%", "50%", "75%", "100%"] }] },
  { label: "Fluids", chartingParameters: [{ parameterId: "amount", label: "Amount consumed (oz)", type: "number", options: [] }] },
  { label: "Bladder", chartingParameters: [{ parameterId: "times", label: "Bladder times", type: "number", options: [] }] },
  { label: "Bowel", chartingParameters: [{ parameterId: "size", label: "Bowel size", type: "select", options: ["S", "M", "L", "D"] }] },
  { label: "Salon Service", chartingParameters: [{ parameterId: "service", label: "Service", type: "select", options: ["RS", "HC", "P"] }] },
  { label: "Bathing", chartingParameters: [{ parameterId: "method", label: "Method", type: "select", options: ["Shower", "Sponge bath"] }] },
  { label: "Dressing", chartingParameters: [{ parameterId: "clothing", label: "Clothing", type: "select", options: ["Upper body", "Lower body", "Full body"] }] },
  { label: "Toileting", chartingParameters: [{ parameterId: "equipment", label: "Equipment", type: "select", options: ["Toilet", "Commode", "Brief"] }] },
  { label: "Oral Hygiene", chartingParameters: [{ parameterId: "completion", label: "Completion", type: "select", options: ["Completed", "Partial", "Refused"] }] },
  { label: "Mobility", chartingParameters: [{ parameterId: "device", label: "Device", type: "select", options: ["None", "Cane", "Walker", "Wheelchair"] }] },
  { label: "Transfers", chartingParameters: [{ parameterId: "method", label: "Transfer method", type: "select", options: ["Independent", "One-person assist", "Two-person assist", "Mechanical lift"] }] },
  { label: "Repositioning", chartingParameters: [{ parameterId: "position", label: "Position", type: "select", options: ["Left", "Right", "Back", "Chair"] }] },
  { label: "Sleep", chartingParameters: [{ parameterId: "quality", label: "Sleep quality", type: "select", options: ["Good", "Fair", "Poor"] }] },
  { label: "Ambulation/Mobility", chartingParameters: [{ parameterId: "location", label: "Location", type: "select", options: ["Room", "Facility", "Outside"] }, { parameterId: "device", label: "Device", type: "select", options: ["None", "FWW", "Walker", "Wheelchair"] }] },
  { label: "Mobility Within Facility", chartingParameters: [{ parameterId: "location", label: "Location", type: "select", options: ["Room", "Facility", "Outside"] }, { parameterId: "device", label: "Device", type: "select", options: ["None", "FWW", "Walker", "Wheelchair"] }] },
  { label: "Mobility Outside Facility", chartingParameters: [{ parameterId: "location", label: "Location", type: "select", options: ["Room", "Facility", "Outside"] }, { parameterId: "device", label: "Device", type: "select", options: ["None", "FWW", "Walker", "Wheelchair"] }] },
  { label: "Bed Mobility/Transfer", chartingParameters: [{ parameterId: "method", label: "Transfer method", type: "select", options: ["Independent", "Pivot", "Stand-pivot", "Hoyer"] }] },
  { label: "Eating", chartingParameters: [{ parameterId: "intake", label: "Intake", type: "select", options: ["0%", "25%", "50%", "75%", "100%"] }, { parameterId: "meal", label: "Meal", type: "select", options: ["Breakfast", "Lunch", "Dinner", "Snack"] }] },
  { label: "Toileting/Continence Issues", chartingParameters: [{ parameterId: "bladderEpisodes", label: "Bladder episodes", type: "number", options: [] }, { parameterId: "bowelSize", label: "Bowel size", type: "select", options: ["S", "M", "L", "D"] }, { parameterId: "equipment", label: "Equipment", type: "select", options: ["Toilet", "Commode", "Brief"] }] },
  { label: "Personal Hygiene", chartingParameters: [{ parameterId: "activity", label: "Activity", type: "select", options: ["Oral care", "Hair care", "Denture care", "Hand care"] }, { parameterId: "completion", label: "Completion", type: "select", options: ["Completed", "Partial", "Refused"] }] },
  { label: "Foot Care", chartingParameters: [{ parameterId: "service", label: "Service", type: "select", options: ["Nail care", "Filing", "Cleaning"] }, { parameterId: "completion", label: "Completion", type: "select", options: ["Completed", "Partial", "Not done"] }] },
  { label: "Skin Care", chartingParameters: [{ parameterId: "area", label: "Area", type: "select", options: ["Groin", "Perineal", "General"] }, { parameterId: "condition", label: "Condition", type: "select", options: ["Intact", "Rash", "Redness", "Open area"] }, { parameterId: "care", label: "Care provided", type: "select", options: ["Cleaned", "Lotion", "Barrier cream"] }] },
  { label: "Turning and Repositioning", chartingParameters: [{ parameterId: "position", label: "Position", type: "select", options: ["Left", "Right", "Back", "Chair"] }, { parameterId: "frequency", label: "Frequency", type: "select", options: ["Every 2 hours", "Every 4 hours", "As needed"] }] },
  { label: "Night Time Care", chartingParameters: [{ parameterId: "care", label: "Care provided", type: "select", options: ["Redirection", "Incontinence care", "Commode setup", "Comfort measures"] }, { parameterId: "frequency", label: "Frequency", type: "select", options: ["Once", "Every 2 hours", "Every 2-4 hours", "As needed"] }] },
  { label: "Managing Finances", chartingParameters: [{ parameterId: "activity", label: "Activity", type: "select", options: ["Budgeting", "Bill payment", "Record keeping", "Money management"] }] },
  { label: "Shopping", chartingParameters: [{ parameterId: "transportation", label: "Transportation", type: "select", options: ["None", "Family", "Staff", "Special transport"] }, { parameterId: "frequency", label: "Frequency", type: "select", options: ["As needed", "Weekly", "Monthly"] }] },
  { label: "Transportation", chartingParameters: [{ parameterId: "purpose", label: "Purpose", type: "select", options: ["Medical services", "Shopping", "Community outing", "Other"] }, { parameterId: "equipment", label: "Equipment", type: "select", options: ["None", "Walker", "Wheelchair", "FWW"] }, { parameterId: "escort", label: "Escort", type: "select", options: ["Not required", "Required"] }] },
].map((item) => ({ ...item, chartingParameters: item.chartingParameters.map((parameter) => ({ ...parameter, parameterId: `${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-${parameter.parameterId}` })) }));

const sortAdlItemSuggestions = (items: any[]) => [...items].sort((first, second) =>
  String(first?.label || "").localeCompare(String(second?.label || ""), undefined, { sensitivity: "base" }),
);

const adlFrequencyOptions = [
  { label: "Once daily", value: "once_daily" },
  { label: "Twice daily", value: "twice_daily" },
  { label: "Three times daily", value: "three_times_daily" },
  { label: "Four times daily", value: "four_times_daily" },
  { label: "At bedtime", value: "bedtime" },
  { label: "Weekly", value: "weekly" },
  { label: "Custom", value: "custom" },
];

const adlChartStyleOptions = [
  { label: "Status only", value: "status_only" },
  { label: "Status + choose one detail", value: "single_subtype" },
];

const adlScheduleDayPatternOptions = [
  { label: "Every day", value: "daily" },
  { label: "Specific days", value: "specific_days" },
  { label: "Monthly dates", value: "specific_month_days" },
  { label: "Alternate days", value: "alternate_days" },
];

const adlWeekdayOptions = [
  { label: "Mon", value: "mon" },
  { label: "Tue", value: "tue" },
  { label: "Wed", value: "wed" },
  { label: "Thu", value: "thu" },
  { label: "Fri", value: "fri" },
  { label: "Sat", value: "sat" },
  { label: "Sun", value: "sun" },
];

const adlScheduleMonthDayOptions = Array.from({ length: 31 }, (_, index) => {
  const day = index + 1;
  return {
    label: String(day),
    value: day,
  };
});

const LOS_ANGELES_DATE_ONLY_FORMATTER = new Intl.DateTimeFormat("en-CA", {
  timeZone: "America/Los_Angeles",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

const adlScheduleTimeOptions = Array.from({ length: 24 * 4 }, (_, index) => {
  const hours = Math.floor(index / 4);
  const minutes = (index % 4) * 15;
  const value = `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
  const displayHours = hours % 12 || 12;
  return {
    label: `${displayHours}:${String(minutes).padStart(2, "0")} ${hours < 12 ? "AM" : "PM"}`,
    value,
  };
});

const defaultAdlScheduleTimes: Record<string, string[]> = {
  once_daily: ["08:00"],
  twice_daily: ["08:00", "20:00"],
  three_times_daily: ["08:00", "14:00", "20:00"],
  four_times_daily: ["08:00", "12:00", "16:00", "20:00"],
  bedtime: ["20:00"],
  weekly: ["09:00"],
};

const parseAdlScheduleTimes = (value: any) => Array.from(new Set(
  String(value || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean),
)).sort();

const normalizeAdlScheduleDayPattern = (value: any) => {
  const normalized = String(value || "").trim().toLowerCase();
  return ["daily", "specific_days", "specific_month_days", "alternate_days"].includes(normalized) ? normalized : "";
};

const normalizeAdlScheduleDays = (value: any) => {
  const aliases: Record<string, string> = {
    monday: "mon",
    mon: "mon",
    tuesday: "tue",
    tue: "tue",
    tues: "tue",
    wednesday: "wed",
    wed: "wed",
    thursday: "thu",
    thu: "thu",
    thur: "thu",
    thurs: "thu",
    friday: "fri",
    fri: "fri",
    saturday: "sat",
    sat: "sat",
    sunday: "sun",
    sun: "sun",
  };
  return Array.from(new Set(Array.isArray(value) ? value : []))
    .map((item) => aliases[String(item || "").trim().toLowerCase()] || String(item || "").trim().toLowerCase())
    .filter((item) => adlWeekdayOptions.some((option) => option.value === item));
};

const normalizeAdlScheduleMonthDays = (value: any) => Array.from(new Set(Array.isArray(value) ? value : []))
  .map((item) => Number(item))
  .filter((item) => Number.isInteger(item) && item >= 1 && item <= 31)
  .sort((left, right) => left - right);

const getLosAngelesDateOnlyCalendarValue = (value: any) => {
  const normalized = String(value || "").trim();
  if (!normalized) {
    return null;
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(normalized)) {
    const parsed = new Date(`${normalized}T12:00:00`);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  return null;
};

const formatLosAngelesDateOnlyFromCalendar = (value: Date | Date[] | null | undefined) => {
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) {
    return "";
  }
  const parts = LOS_ANGELES_DATE_ONLY_FORMATTER.formatToParts(value);
  const year = parts.find((part) => part.type === "year")?.value || "";
  const month = parts.find((part) => part.type === "month")?.value || "";
  const day = parts.find((part) => part.type === "day")?.value || "";
  return year && month && day ? `${year}-${month}-${day}` : "";
};

const getLosAngelesTodayDateString = () => formatLosAngelesDateOnlyFromCalendar(new Date());

const getLosAngelesYesterdayDateString = () => {
  const todayDateKey = getLosAngelesTodayDateString();
  const match = todayDateKey.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) {
    return todayDateKey;
  }
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 12, 0, 0));
  date.setUTCDate(date.getUTCDate() - 1);
  return formatLosAngelesDateOnlyFromCalendar(date);
};

const getLosAngelesSevenDayStartDateString = () => {
  const todayDateKey = getLosAngelesTodayDateString();
  const match = todayDateKey.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return todayDateKey;
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 12, 0, 0));
  date.setUTCDate(date.getUTCDate() - 6);
  return formatLosAngelesDateOnlyFromCalendar(date);
};

const getLosAngelesHistoryEarliestDateString = () => {
  const todayDateKey = getLosAngelesTodayDateString();
  const match = todayDateKey.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) {
    return todayDateKey;
  }
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 12, 0, 0));
  date.setUTCDate(date.getUTCDate() - 90);
  return formatLosAngelesDateOnlyFromCalendar(date);
};

const inferAdlFrequencyPreset = (task: any) => {
  if (String(task?.frequencyPreset || "").trim()) {
    return String(task.frequencyPreset).trim();
  }
  if (String(task?.frequencyType || "").trim() === "weekly_days") {
    return "weekly";
  }
  const times = parseAdlScheduleTimes(task?.scheduledTimesText || (Array.isArray(task?.scheduledTimes) ? task.scheduledTimes.join(",") : ""));
  if (times.length === 1 && times[0] === "20:00") return "bedtime";
  if (times.length === 1) return "once_daily";
  if (times.length === 2) return "twice_daily";
  if (times.length === 3) return "three_times_daily";
  if (times.length === 4) return "four_times_daily";
  return "custom";
};

const adlChartOutcomeOptions = [
  { label: "Record", value: "recorded" },
  { label: "Not Recorded", value: "not_recorded" },
  { label: "Refused", value: "refused" },
  { label: "Hospitalized", value: "hospitalized" },
  { label: "Family", value: "family" },
];

const adlNoteOnlyActionValues = new Set(["not_recorded", "refused", "family", "hospitalized"]);

const adlListFilterOptions = [
  { label: "Active items", value: "active" },
  { label: "Discontinued items", value: "discontinued" },
  { label: "Deleted items", value: "inactive" },
];

const adlPassFilterOptions = [
  { label: "All passes", value: "all" },
  { label: "Pending", value: "pending" },
  { label: "Charted", value: "charted" },
];

const LOS_ANGELES_MONTH_VALUE_FORMATTER = new Intl.DateTimeFormat("en-CA", {
  timeZone: "America/Los_Angeles",
  year: "numeric",
  month: "2-digit",
});

const LOS_ANGELES_MONTH_LABEL_FORMATTER = new Intl.DateTimeFormat("en-US", {
  timeZone: "America/Los_Angeles",
  month: "long",
  year: "numeric",
});

const formatElapsedTime = (totalSeconds: number) => {
  const seconds = Math.max(0, Math.floor(Number(totalSeconds) || 0));
  if (seconds < 60) return `${seconds} sec`;
  const minutes = Math.floor(seconds / 60);
  return `${minutes} min ${seconds % 60} sec`;
};

const formatLosAngelesDateTime = (value: any) => {
  const date = new Date(String(value || "").trim());
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  return new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Los_Angeles",
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
};

const buildLosAngelesReportMonthOptions = () => {
  const now = new Date();
  const currentYear = Number(new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Los_Angeles",
    year: "numeric",
  }).format(now));
  const currentMonthIndex = Number(new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Los_Angeles",
    month: "2-digit",
  }).format(now)) - 1;

  return Array.from({ length: 12 }, (_, index) => {
    const targetDate = new Date(Date.UTC(currentYear, currentMonthIndex - index, 15, 12, 0, 0));
    return {
      value: LOS_ANGELES_MONTH_VALUE_FORMATTER.format(targetDate),
      label: LOS_ANGELES_MONTH_LABEL_FORMATTER.format(targetDate),
    };
  });
};

const buildTaskDraftsFromTemplate = (template: any) => (
  (Array.isArray(template?.tasks) ? template.tasks : []).map((task: any, index: number) => ({
    taskId: String(task?.taskId || `adl-task-${index + 1}`).trim(),
    label: String(task?.label || "").trim(),
    taskType: String(task?.taskType || "adl").trim() || "adl",
    supportLevel: String(task?.supportLevel || "").trim(),
    instructions: String(task?.instructions || "").trim(),
    precautions: String(task?.precautions || "").trim(),
    enabled: task?.enabled !== false,
    source: String(task?.source || "manual").trim() || "manual",
    displayOrder: Number.isFinite(Number(task?.displayOrder)) ? Number(task?.displayOrder) : index,
    chartStyle: String(task?.chartStyle || "status_only").trim() || "status_only",
    frequencyType: String(task?.frequencyType || "daily").trim() || "daily",
    frequencyPreset: String(task?.frequencyPreset || "").trim() || inferAdlFrequencyPreset(task),
    timesPerDay: Number.isFinite(Number(task?.timesPerDay)) ? Number(task?.timesPerDay) : 1,
    effectiveStartDate: String(
      task?.effectiveStartDate
      || (String(task?.source || "").trim().toLowerCase().startsWith("care_plan") ? getLosAngelesTodayDateString() : "")
    ).trim(),
    effectiveEndDate: String(task?.effectiveEndDate || "").trim(),
    discontinued: task?.discontinued === true,
    discontinuedAt: String(task?.discontinuedAt || "").trim(),
    discontinuedReason: String(task?.discontinuedReason || "").trim(),
    scheduledTimes: Array.isArray(task?.scheduledTimes)
      ? task.scheduledTimes.map((value: any) => String(value || "").trim()).filter(Boolean)
      : [],
    scheduledDays: normalizeAdlScheduleDays(task?.scheduledDays),
    scheduleDayPattern: normalizeAdlScheduleDayPattern(task?.scheduleDayPattern)
      || (normalizeAdlScheduleMonthDays(task?.scheduleMonthDays).length ? "specific_month_days" : "")
      || (normalizeAdlScheduleDays(task?.scheduledDays).length ? "specific_days" : "daily"),
    scheduleMonthDays: normalizeAdlScheduleMonthDays(task?.scheduleMonthDays),
    subTypes: Array.isArray(task?.subTypes)
      ? task.subTypes.map((subType: any, subTypeIndex: number) => ({
        subTypeId: String(subType?.subTypeId || `${String(task?.taskId || `adl-task-${index + 1}`).trim()}-subtype-${subTypeIndex + 1}`).trim(),
        code: String(subType?.code || "").trim().toUpperCase(),
        label: String(subType?.label || "").trim(),
        active: subType?.active !== false,
        displayOrder: Number.isFinite(Number(subType?.displayOrder)) ? Number(subType?.displayOrder) : subTypeIndex,
      })).filter((subType: any) => subType.code || subType.label)
      : [],
    chartingParameters: getAdlChartingParametersForCharting(task?.chartingParameters),
    statusOptions: Array.isArray(task?.statusOptions) && task.statusOptions.length
      ? task.statusOptions.map((option: any) => String(option || "").trim()).filter(Boolean)
      : defaultStatusOptions.map((option) => option.value),
  }))
);

const buildEntryDrafts = (publishedTemplate: any, todayEntry: any, todayPassItems?: any[]) => {
  if (Array.isArray(todayPassItems) && todayPassItems.length) {
    return todayPassItems.map((item: any, index: number) => ({
      taskId: String(item?.taskId || `adl-task-${index + 1}`).trim(),
      occurrenceKey: String(item?.occurrenceKey || `${String(item?.taskId || `adl-task-${index + 1}`).trim()}::daily`).trim(),
      occurrenceLabel: String(item?.occurrenceLabel || "Daily Pass").trim(),
      label: String(item?.label || "").trim() || `ADL Task ${index + 1}`,
      source: String(item?.source || "manual").trim() || "manual",
      effectiveStartDate: String(item?.effectiveStartDate || (String(item?.source || "").trim().toLowerCase().startsWith("care_plan") ? getLosAngelesTodayDateString() : "")).trim(),
      effectiveEndDate: String(item?.effectiveEndDate || "").trim(),
      supportLevel: String(item?.supportLevel || "").trim(),
      instructions: String(item?.instructions || "").trim(),
      precautions: String(item?.precautions || "").trim(),
      chartStyle: String(item?.chartStyle || "status_only").trim() || "status_only",
      frequencyType: String(item?.frequencyType || "daily").trim() || "daily",
      timesPerDay: Number.isFinite(Number(item?.timesPerDay)) ? Number(item?.timesPerDay) : 1,
      scheduledTimes: Array.isArray(item?.scheduledTimes)
        ? item.scheduledTimes.map((value: any) => String(value || "").trim()).filter(Boolean)
        : [],
      scheduledDays: Array.isArray(item?.scheduledDays)
        ? item.scheduledDays.map((value: any) => String(value || "").trim().toLowerCase()).filter(Boolean)
        : [],
      subTypes: Array.isArray(item?.subTypes)
        ? item.subTypes.map((subType: any, subTypeIndex: number) => ({
          subTypeId: String(subType?.subTypeId || `${String(item?.taskId || `adl-task-${index + 1}`).trim()}-subtype-${subTypeIndex + 1}`).trim(),
          code: String(subType?.code || "").trim().toUpperCase(),
          label: String(subType?.label || "").trim(),
          active: subType?.active !== false,
          displayOrder: Number.isFinite(Number(subType?.displayOrder)) ? Number(subType?.displayOrder) : subTypeIndex,
        })).filter((subType: any) => subType.code || subType.label)
        : [],
      chartingParameters: getAdlChartingParametersForCharting(item?.chartingParameters),
      parameterValues: item?.parameterValues && typeof item.parameterValues === "object" ? item.parameterValues : {},
      selectedSubTypeCode: String(item?.selectedSubTypeCode || "").trim().toUpperCase(),
      selectedSubTypeCodes: Array.isArray(item?.selectedSubTypeCodes)
        ? item.selectedSubTypeCodes.map((value: any) => String(value || "").trim().toUpperCase()).filter(Boolean)
        : [],
      value: String(item?.value || "").trim(),
      status: String(item?.status || "").trim(),
      note: String(item?.note || "").trim(),
      dateKey: String(item?.dateKey || "").trim(),
      entryVersion: Number.isFinite(Number(item?.entryVersion)) ? Number(item.entryVersion) : 0,
      recordedAt: String(item?.recordedAt || "").trim(),
      statusHistory: Array.isArray(item?.statusHistory) ? item.statusHistory : [],
      missed: item?.missed === true,
      syntheticPending: item?.syntheticPending === true,
      statusMessage: String(item?.statusMessage || "").trim(),
      statusOptions: Array.isArray(item?.statusOptions) && item.statusOptions.length
        ? item.statusOptions.map((option: any) => String(option || "").trim()).filter(Boolean)
        : defaultStatusOptions.map((option) => option.value),
    }));
  }
  const entryByTaskId = new Map<string, any>(
    (Array.isArray(todayEntry?.taskEntries) ? todayEntry.taskEntries : []).map((entry: any) => [String(entry?.taskId || "").trim(), entry]),
  );
  return (Array.isArray(publishedTemplate?.tasks) ? publishedTemplate.tasks : []).map((task: any, index: number) => {
    const taskId = String(task?.taskId || `adl-task-${index + 1}`).trim();
    const existingEntry = entryByTaskId.get(taskId);
    return {
      taskId,
      occurrenceKey: String(existingEntry?.occurrenceKey || `${taskId}::daily`).trim(),
      occurrenceLabel: String(existingEntry?.occurrenceLabel || "Daily Pass").trim(),
      label: String(task?.label || "").trim() || `ADL Task ${index + 1}`,
      source: String(task?.source || "manual").trim() || "manual",
      effectiveStartDate: String(task?.effectiveStartDate || (String(task?.source || "").trim().toLowerCase().startsWith("care_plan") ? getLosAngelesTodayDateString() : "")).trim(),
      effectiveEndDate: String(task?.effectiveEndDate || "").trim(),
      supportLevel: String(task?.supportLevel || "").trim(),
      instructions: String(task?.instructions || "").trim(),
      precautions: String(task?.precautions || "").trim(),
      chartStyle: String(task?.chartStyle || "status_only").trim() || "status_only",
      frequencyType: String(task?.frequencyType || "daily").trim() || "daily",
      timesPerDay: Number.isFinite(Number(task?.timesPerDay)) ? Number(task?.timesPerDay) : 1,
      scheduledTimes: Array.isArray(task?.scheduledTimes)
        ? task.scheduledTimes.map((value: any) => String(value || "").trim()).filter(Boolean)
        : [],
      scheduledDays: Array.isArray(task?.scheduledDays)
        ? task.scheduledDays.map((value: any) => String(value || "").trim().toLowerCase()).filter(Boolean)
        : [],
      subTypes: Array.isArray(task?.subTypes)
        ? task.subTypes.map((subType: any, subTypeIndex: number) => ({
          subTypeId: String(subType?.subTypeId || `${taskId}-subtype-${subTypeIndex + 1}`).trim(),
          code: String(subType?.code || "").trim().toUpperCase(),
          label: String(subType?.label || "").trim(),
          active: subType?.active !== false,
          displayOrder: Number.isFinite(Number(subType?.displayOrder)) ? Number(subType?.displayOrder) : subTypeIndex,
        })).filter((subType: any) => subType.code || subType.label)
        : [],
      chartingParameters: getAdlChartingParametersForCharting(task?.chartingParameters),
      parameterValues: existingEntry?.parameterValues && typeof existingEntry.parameterValues === "object" ? existingEntry.parameterValues : {},
      selectedSubTypeCode: String(existingEntry?.subTypeCode || "").trim().toUpperCase(),
      selectedSubTypeCodes: Array.isArray(existingEntry?.subTypeCodes)
        ? existingEntry.subTypeCodes.map((value: any) => String(value || "").trim().toUpperCase()).filter(Boolean)
        : [],
      value: String(existingEntry?.value || "").trim(),
      status: String(existingEntry?.status || "").trim(),
      note: String(existingEntry?.note || "").trim(),
      dateKey: String(todayEntry?.dateKey || "").trim(),
      entryVersion: Number.isFinite(Number(todayEntry?.version)) ? Number(todayEntry.version) : 0,
      recordedAt: String(existingEntry?.recordedAt || "").trim(),
      statusHistory: Array.isArray(existingEntry?.statusHistory) ? existingEntry.statusHistory : [],
      statusOptions: Array.isArray(task?.statusOptions) && task.statusOptions.length
        ? task.statusOptions.map((option: any) => String(option || "").trim()).filter(Boolean)
        : defaultStatusOptions.map((option) => option.value),
    };
  });
};

const getAdlScheduleTimeSortValue = (rowData: any) => {
  const scheduledTimes = Array.isArray(rowData?.scheduledTimes)
    ? rowData.scheduledTimes.map((value: any) => String(value || "").trim()).filter(Boolean)
    : [];
  const occurrenceLabel = String(rowData?.occurrenceLabel || "").trim();
  const timeCandidates = [...scheduledTimes, occurrenceLabel];

  for (const candidate of timeCandidates) {
    const match = candidate.match(/\b(\d{1,2}):(\d{2})(?:\s*([ap]m))?\b/i);
    if (!match) {
      continue;
    }
    let hours = Number(match[1]);
    const minutes = Number(match[2]);
    const meridiem = String(match[3] || "").toLowerCase();
    if (meridiem === "pm" && hours < 12) hours += 12;
    if (meridiem === "am" && hours === 12) hours = 0;
    if (hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59) {
      return (hours * 60) + minutes;
    }
  }

  return Number.MAX_SAFE_INTEGER;
};

const sortAdlRows = (rows: any[], sortBy: "task" | "time") => [...rows].sort((first, second) => {
  const firstLabel = String(first?.label || "").trim();
  const secondLabel = String(second?.label || "").trim();
  if (sortBy === "time") {
    const timeDifference = getAdlScheduleTimeSortValue(first) - getAdlScheduleTimeSortValue(second);
    return timeDifference || firstLabel.localeCompare(secondLabel);
  }
  return firstLabel.localeCompare(secondLabel) || (getAdlScheduleTimeSortValue(first) - getAdlScheduleTimeSortValue(second));
});

const ProviderResidentAdlPage = () => {
  const { residentId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { backendAPIStore, authStore } = useContext(MobXProviderContext);
  const toast = useRef<Toast>(null);
  const mobile = useResponsive().isMobile;

  const userRoleType = authStore.getUserRoleType();
  const isAdminUser = authStore.isAdmin();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [exportingReport, setExportingReport] = useState(false);
  const [processingGeneration, setProcessingGeneration] = useState(false);
  const [generationElapsedSeconds, setGenerationElapsedSeconds] = useState(0);
  const [showGenerateTasksDialog, setShowGenerateTasksDialog] = useState(false);
  const [showMissingCarePlanDialog, setShowMissingCarePlanDialog] = useState(false);
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [showChartEntryDialog, setShowChartEntryDialog] = useState(false);
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const archivedReadOnly = searchParams.get("archived") === "true";
  const [historyDateKey, setHistoryDateKey] = useState(getLosAngelesYesterdayDateString());
  const [historyStatusFilter, setHistoryStatusFilter] = useState<"all" | "missed">("all");
  const [historyEntryDrafts, setHistoryEntryDrafts] = useState<any[]>([]);
  const [adlHistoryDialogVisible, setAdlHistoryDialogVisible] = useState(false);
  const [adlTaskHistoryDialogVisible, setAdlTaskHistoryDialogVisible] = useState(false);
  const [selectedAdlHistoryEntry, setSelectedAdlHistoryEntry] = useState<any | null>(null);
  const [adlEntryToDelete, setAdlEntryToDelete] = useState<any | null>(null);
  const [deletingAdlEntry, setDeletingAdlEntry] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [selectedReportMonth, setSelectedReportMonth] = useState(LOS_ANGELES_MONTH_VALUE_FORMATTER.format(new Date()));
  const [reportRangeMode, setReportRangeMode] = useState<"monthly" | "last7Days" | "custom">("monthly");
  const [reportStartDate, setReportStartDate] = useState(getLosAngelesSevenDayStartDateString());
  const [reportEndDate, setReportEndDate] = useState(getLosAngelesTodayDateString());
  const [blankAdlReport, setBlankAdlReport] = useState(false);
  const [adlData, setAdlData] = useState<any>(null);
  const [draftTasks, setDraftTasks] = useState<any[]>([]);
  const [entryDrafts, setEntryDrafts] = useState<any[]>([]);
  const [showCustomTaskDialog, setShowCustomTaskDialog] = useState(false);
  const [editingTaskId, setEditingTaskId] = useState<string>("");
  const [adlListFilter, setAdlListFilter] = useState<"active" | "discontinued" | "inactive">("active");
  const [todayPassFilter, setTodayPassFilter] = useState<"all" | "pending" | "charted">("all");
  const [todayPassSortBy, setTodayPassSortBy] = useState<"task" | "time">("time");
  const [adlListSortBy, setAdlListSortBy] = useState<"task" | "time">("task");
  const [adlItemSuggestions, setAdlItemSuggestions] = useState<any[]>(sortAdlItemSuggestions(adlItemExamples));
  const [mobileAdlDetails, setMobileAdlDetails] = useState<Record<string, boolean>>({});
  const [suppressTodayPassAfterReset, setSuppressTodayPassAfterReset] = useState(false);
  const [customTask, setCustomTask] = useState<any>({
    label: "",
    supportLevel: "",
    instructions: "",
    precautions: "",
    chartStyle: "status_only",
    frequencyType: "scheduled_times",
    frequencyPreset: "once_daily",
    timesPerDay: 1,
    effectiveStartDate: getLosAngelesTodayDateString(),
    effectiveEndDate: "",
    discontinued: false,
    discontinuedAt: "",
    discontinuedReason: "",
    scheduledTimesText: "08:00",
    scheduleDayPattern: "daily",
    scheduledDays: [],
    scheduleMonthDays: [],
    subTypes: [],
    chartingParameters: [],
  });
  const [subTypeDraft, setSubTypeDraft] = useState({ code: "", label: "" });
  const [showChartingParameterDialog, setShowChartingParameterDialog] = useState(false);
  const [chartingParameterDraft, setChartingParameterDraft] = useState<any>({ parameterId: "", label: "", type: "select", optionsText: "" });
  const [editingChartingParameterId, setEditingChartingParameterId] = useState("");
  const [applyHospitalizedToAllForDay, setApplyHospitalizedToAllForDay] = useState(false);
  const [chartEntryDraft, setChartEntryDraft] = useState<any>(null);
  const [adlNow, setAdlNow] = useState(() => Date.now());

  const resolvedHomeId = String(searchParams.get("homeId") || adlData?.homeId || "").trim();
  const reportMonthOptions = useMemo(() => buildLosAngelesReportMonthOptions(), []);

  useEffect(() => {
    const timerId = window.setInterval(() => setAdlNow(Date.now()), 30 * 1000);
    return () => window.clearInterval(timerId);
  }, []);

  const searchAdlItemSuggestions = (event: any) => {
    const query = String(event?.query || "").trim().toLowerCase();
    setAdlItemSuggestions(sortAdlItemSuggestions(query
      ? adlItemExamples.filter((item) => item.label.toLowerCase().includes(query))
      : adlItemExamples));
  };

  const applyAdlItemExample = (item: any) => {
    setCustomTask((current: any) => ({
      ...current,
      label: item?.label || current.label,
      chartingParameters: getAdlChartingParametersForCharting(item?.chartingParameters),
    }));
  };

  const isAdlChartOutsideOneHourWindow = (rowData: any) => {
    const match = String(rowData?.occurrenceLabel || "").trim().match(/^(\d{1,2}):(\d{2})$/);
    const rowDateKey = String(rowData?.dateKey || adlData?.todayDateKey || "").trim();
    if (!match || rowDateKey !== getLosAngelesTodayDateString()) return false;
    const currentParts = new Intl.DateTimeFormat("en-US", { timeZone: "America/Los_Angeles", hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).formatToParts(new Date(adlNow));
    const currentMinutes = (Number(currentParts.find((part) => part.type === "hour")?.value || 0) * 60) + Number(currentParts.find((part) => part.type === "minute")?.value || 0);
    const scheduledMinutes = (Number(match[1]) * 60) + Number(match[2]);
    return Math.abs(currentMinutes - scheduledMinutes) > 60;
  };

  const getAdlChartTimingDifferenceHours = (rowData: any) => {
    const match = String(rowData?.occurrenceLabel || "").trim().match(/^(\d{1,2}):(\d{2})$/);
    if (!match) return 0;
    const currentParts = new Intl.DateTimeFormat("en-US", { timeZone: "America/Los_Angeles", hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).formatToParts(new Date());
    const currentMinutes = (Number(currentParts.find((part) => part.type === "hour")?.value || 0) * 60) + Number(currentParts.find((part) => part.type === "minute")?.value || 0);
    return Math.abs(currentMinutes - ((Number(match[1]) * 60) + Number(match[2]))) / 60;
  };

  const isAdlOccurrenceScheduledInFuture = (rowData: any) => {
    const match = String(rowData?.occurrenceLabel || "").trim().match(/^(\d{1,2}):(\d{2})$/);
    const rowDateKey = String(rowData?.dateKey || adlData?.todayDateKey || "").trim();
    if (!match || rowDateKey !== getLosAngelesTodayDateString()) return false;
    const currentParts = new Intl.DateTimeFormat("en-US", { timeZone: "America/Los_Angeles", hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).formatToParts(new Date(adlNow));
    const currentMinutes = (Number(currentParts.find((part) => part.type === "hour")?.value || 0) * 60) + Number(currentParts.find((part) => part.type === "minute")?.value || 0);
    return currentMinutes < (Number(match[1]) * 60) + Number(match[2]);
  };

  const isAdlOccurrenceUnavailableBeforeWindow = (rowData: any) => {
    const match = String(rowData?.occurrenceLabel || "").trim().match(/^(\d{1,2}):(\d{2})$/);
    const rowDateKey = String(rowData?.dateKey || adlData?.todayDateKey || "").trim();
    if (!match || rowDateKey !== getLosAngelesTodayDateString()) return false;
    const currentParts = new Intl.DateTimeFormat("en-US", { timeZone: "America/Los_Angeles", hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).formatToParts(new Date(adlNow));
    const currentMinutes = (Number(currentParts.find((part) => part.type === "hour")?.value || 0) * 60) + Number(currentParts.find((part) => part.type === "minute")?.value || 0);
    const scheduledMinutes = (Number(match[1]) * 60) + Number(match[2]);
    return currentMinutes < scheduledMinutes - 60;
  };

  const loadAdlTracking = async (requestedHistoryDateKey = historyDateKey) => {
    if (!residentId) {
      return;
    }
    setLoading(true);
    try {
      await backendAPIStore.init();
      const response = await backendAPIStore.getResidentAdlTracking(residentId, { historyDateKey: requestedHistoryDateKey });
      setAdlData(response || null);
      setSuppressTodayPassAfterReset(Boolean(response?.hideTodayPass));
      const initialDraftTasks = buildTaskDraftsFromTemplate(response?.draftTemplate || response?.publishedTemplate);
      const initialEntryDrafts = buildEntryDrafts(response?.publishedTemplate, response?.todayEntry, response?.todayPassItems);
      setDraftTasks(initialDraftTasks);
      setEntryDrafts(initialEntryDrafts);
      setHistoryDateKey(String(response?.historyDateKey || requestedHistoryDateKey).trim());
      setHistoryEntryDrafts(buildEntryDrafts(response?.publishedTemplate, response?.historyEntry, response?.historyPassItems));
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: getErrorTitle(error),
        detail: error?.message || "Unable to load ADL tracking.",
        life: 3500,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadAdlTracking();
  }, [residentId]);

  const loadAdlHistory = async (requestedHistoryDateKey: string) => {
    if (!residentId) {
      return;
    }
    setLoadingHistory(true);
    try {
      await backendAPIStore.init();
      const response = await backendAPIStore.getResidentAdlTracking(residentId, { historyDateKey: requestedHistoryDateKey });
      setAdlData((currentData: any) => ({ ...(currentData || {}), ...(response || {}) }));
      setHistoryDateKey(String(response?.historyDateKey || requestedHistoryDateKey).trim());
      setHistoryEntryDrafts(buildEntryDrafts(response?.publishedTemplate, response?.historyEntry, response?.historyPassItems));
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: getErrorTitle(error),
        detail: error?.message || "Unable to load past ADL entries.",
        life: 3500,
      });
      setHistoryEntryDrafts([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  useEffect(() => {
    if (activeTabIndex === 2 && adlData) {
      void loadAdlHistory(historyDateKey);
    }
  }, [activeTabIndex, historyDateKey]);

  const hasDraftTasks = draftTasks.length > 0;
  const visibleEntryDrafts = suppressTodayPassAfterReset ? [] : entryDrafts;
  const hasPublishedTasks = Array.isArray(adlData?.publishedTemplate?.tasks) && adlData.publishedTemplate.tasks.length > 0;
  const hasAnyAdlTasks = hasDraftTasks || hasPublishedTasks;
  const hasResettableTasks = hasDraftTasks || entryDrafts.length > 0 || hasPublishedTasks;
  const activeDraftTasks = useMemo(
    () => draftTasks.filter((task: any) => task?.enabled !== false && task?.discontinued !== true),
    [draftTasks],
  );
  const discontinuedDraftTasks = useMemo(
    () => draftTasks.filter((task: any) => task?.enabled !== false && task?.discontinued === true),
    [draftTasks],
  );
  const deletedDraftTasks = useMemo(
    () => draftTasks.filter((task: any) => task?.enabled === false),
    [draftTasks],
  );
  const displayedDraftTasks = useMemo(() => {
    if (adlListFilter === "inactive") {
      return deletedDraftTasks;
    }
    if (adlListFilter === "discontinued") {
      return discontinuedDraftTasks;
    }
    return activeDraftTasks;
  }, [adlListFilter, activeDraftTasks, discontinuedDraftTasks, deletedDraftTasks]);
  const filteredVisibleEntryDrafts = useMemo(() => {
    if (todayPassFilter === "all") {
      return visibleEntryDrafts;
    }
    return visibleEntryDrafts.filter((entry: any) => {
      const isCharted = Boolean(String(entry?.status || "").trim());
      return todayPassFilter === "charted" ? isCharted : !isCharted;
    });
  }, [todayPassFilter, visibleEntryDrafts]);
  const sortedTodayEntryDrafts = useMemo(
    () => {
      const grouped = new Map<string, any>();
      filteredVisibleEntryDrafts.forEach((entry: any) => {
        const taskId = String(entry?.taskId || "").trim();
        const existing = grouped.get(taskId);
        if (existing) {
          existing.occurrences.push(entry);
        } else {
          grouped.set(taskId, { ...entry, occurrences: [entry] });
        }
      });
      return sortAdlRows(Array.from(grouped.values()), todayPassSortBy);
    },
    [filteredVisibleEntryDrafts, todayPassSortBy],
  );

  const sortedDisplayedDraftTasks = useMemo(
    () => sortAdlRows(displayedDraftTasks, adlListSortBy),
    [displayedDraftTasks, adlListSortBy],
  );
  const latestGenerationJob = adlData?.latestGenerationJob || null;
  const todayChartedCount = visibleEntryDrafts.filter((entry) => String(entry?.status || "").trim()).length;
  const todayPendingCount = Math.max(visibleEntryDrafts.length - todayChartedCount, 0);
  const filteredHistoryEntryDrafts = useMemo(() => (
    historyStatusFilter === "missed"
      ? historyEntryDrafts.filter((entry: any) => entry?.missed === true || String(entry?.status || "").trim().toLowerCase() === "missed")
      : historyEntryDrafts
  ), [historyStatusFilter, historyEntryDrafts]);
  const historyChartedCount = filteredHistoryEntryDrafts.filter((entry) => String(entry?.status || "").trim()).length;
  const historyPendingCount = Math.max(filteredHistoryEntryDrafts.length - historyChartedCount, 0);
  const historyDateValue = getLosAngelesDateOnlyCalendarValue(historyDateKey);
  const historyMinDateValue = getLosAngelesDateOnlyCalendarValue(getLosAngelesHistoryEarliestDateString());
  const historyMaxDateValue = getLosAngelesDateOnlyCalendarValue(getLosAngelesTodayDateString());

  const latestGenerationTag = useMemo(() => {
    const jobStatus = String(adlData?.latestGenerationJob?.jobStatus || "").trim();
    if (!jobStatus) {
      return null;
    }
    const normalizedStatus = jobStatus.toUpperCase();
    return (
      <Tag
        value={`Generation ${jobStatus.toLowerCase()}`}
        severity={normalizedStatus === "COMPLETED" ? "success" : normalizedStatus === "FAILED" ? "danger" : "info"}
      />
    );
  }, [adlData?.latestGenerationJob?.jobStatus]);

  useEffect(() => {
    const latestJobId = String(adlData?.latestGenerationJob?.jobId || "").trim();
    const latestJobStatus = String(adlData?.latestGenerationJob?.jobStatus || "").trim().toUpperCase();
    if (!latestJobId || !["SUBMITTED", "IN_PROGRESS", "RETRYING"].includes(latestJobStatus)) {
      setProcessingGeneration(false);
      return undefined;
    }

    setProcessingGeneration(true);
    let cancelled = false;
    const poll = async () => {
      try {
        const jobDetails = await backendAPIStore.getJobDetails(latestJobId);
        if (cancelled) {
          return;
        }
        const nextStatus = String(jobDetails?.jobStatus || "").trim().toUpperCase();
        setAdlData((currentData: any) => ({
          ...(currentData || {}),
          latestGenerationJob: {
            ...(currentData?.latestGenerationJob || {}),
            jobId: latestJobId,
            jobStatus: nextStatus || String(jobDetails?.jobStatus || "").trim(),
            jobSubStatus: String(jobDetails?.jobSubStatus || currentData?.latestGenerationJob?.jobSubStatus || "").trim(),
            createdAt: String(jobDetails?.createdAt || currentData?.latestGenerationJob?.createdAt || currentData?.latestGenerationJob?.generatedAt || "").trim(),
            generatedAt: String(jobDetails?.completedAt || jobDetails?.modifiedAt || jobDetails?.createdAt || currentData?.latestGenerationJob?.generatedAt || "").trim(),
            taskCount: Number(jobDetails?.resultSummary?.taskCount || currentData?.latestGenerationJob?.taskCount || 0),
            errorMessage: String(jobDetails?.errorMessage || "").trim(),
          },
        }));

        if (nextStatus === "COMPLETED") {
          setProcessingGeneration(false);
          await loadAdlTracking();
          toast.current?.show({
            severity: "success",
            summary: "ADL Tasks Ready",
            detail: "Draft ADL tasks were generated from the care plan.",
            life: 3000,
          });
          return;
        }
        if (nextStatus === "FAILED") {
          setProcessingGeneration(false);
          toast.current?.show({
            severity: "error",
            summary: "Generation failed",
            detail: String(jobDetails?.errorMessage || "Unable to generate ADL tasks from the care plan."),
            life: 4000,
          });
          return;
        }
        window.setTimeout(() => { void poll(); }, 2500);
      } catch (error: any) {
        if (cancelled) {
          return;
        }
        setProcessingGeneration(false);
        toast.current?.show({
          severity: "error",
          summary: getErrorTitle(error),
          detail: error?.message || "Unable to check ADL generation status.",
          life: 3500,
        });
      }
    };

    window.setTimeout(() => { void poll(); }, 1000);
    return () => {
      cancelled = true;
    };
  }, [adlData?.latestGenerationJob?.jobId, adlData?.latestGenerationJob?.jobStatus, backendAPIStore]);

  useEffect(() => {
    const openGenerateDialog = () => { handleOpenGenerateTasksDialog(); };
    const openCustomDialog = () => {
      resetCustomTaskDialog();
      setShowCustomTaskDialog(true);
    };
    const saveWorkingSetup = () => { void handleSaveDraft(); };
    const publishLiveSetup = () => { void handlePublish(); };
    const openCarePlan = () => navigate(RouterAddress.getProviderResidentCarePlanPagePath(String(residentId || ""), resolvedHomeId));
    const downloadReport = () => setShowReportDialog(true);

    window.addEventListener("resident-adl:open-generate-dialog", openGenerateDialog as EventListener);
    window.addEventListener("resident-adl:open-custom-dialog", openCustomDialog as EventListener);
    window.addEventListener("resident-adl:save-working-setup", saveWorkingSetup as EventListener);
    window.addEventListener("resident-adl:publish-live-setup", publishLiveSetup as EventListener);
    window.addEventListener("resident-adl:open-care-plan", openCarePlan as EventListener);
    window.addEventListener("resident-adl:request-download-report", downloadReport as EventListener);
    return () => {
      window.removeEventListener("resident-adl:open-generate-dialog", openGenerateDialog as EventListener);
      window.removeEventListener("resident-adl:open-custom-dialog", openCustomDialog as EventListener);
      window.removeEventListener("resident-adl:save-working-setup", saveWorkingSetup as EventListener);
      window.removeEventListener("resident-adl:publish-live-setup", publishLiveSetup as EventListener);
      window.removeEventListener("resident-adl:open-care-plan", openCarePlan as EventListener);
      window.removeEventListener("resident-adl:request-download-report", downloadReport as EventListener);
    };
  }, [adlData?.latestGenerationJob?.completedAt, adlData?.latestGenerationJob?.generatedAt, adlData?.latestGenerationJob?.jobStatus, navigate, residentId, resolvedHomeId]);

  const updateDraftTask = (taskId: string, field: string, value: any) => {
    setDraftTasks((currentTasks) => currentTasks.map((task) => (
      task.taskId === taskId
        ? {
          ...task,
          [field]: value,
        }
        : task
    )));
  };

  const removeDraftTask = (taskId: string) => {
    setDraftTasks((currentTasks) => currentTasks.filter((task) => task.taskId !== taskId));
  };

  const saveDraftTasks = async (nextTasks: any[], successDetail = "ADL setup draft updated.") => {
    if (archivedReadOnly) return false;
    if (!residentId || !resolvedHomeId) {
      return false;
    }
    const previousTasks = draftTasks;
    setSaving(true);
    try {
      await backendAPIStore.init();
      const response = await backendAPIStore.saveResidentAdlDraft({
        residentId,
        homeId: resolvedHomeId,
        draftTemplate: {
          source: adlData?.draftTemplate?.source || "manual",
          tasks: nextTasks,
        },
      });
      setSuppressTodayPassAfterReset(false);
      setAdlData(response || null);
      setDraftTasks(buildTaskDraftsFromTemplate(response?.draftTemplate || response?.publishedTemplate));
      const refreshedEntryDrafts = buildEntryDrafts(response?.publishedTemplate, response?.todayEntry, response?.todayPassItems);
      setEntryDrafts(refreshedEntryDrafts);
      toast.current?.show({ severity: "success", summary: "Draft Saved", detail: successDetail, life: 2500 });
      return true;
    } catch (error: any) {
      setDraftTasks(previousTasks);
      toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message || "Unable to save ADL draft.", life: 3500 });
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDraft = async () => {
    if (archivedReadOnly) return;
    await saveDraftTasks(draftTasks);
  };

  const handlePublish = async () => {
    if (!residentId || !resolvedHomeId) {
      return;
    }
    setSaving(true);
    try {
      await backendAPIStore.init();
      const response = await backendAPIStore.publishResidentAdlDraft({
        residentId,
        homeId: resolvedHomeId,
      });
      setSuppressTodayPassAfterReset(false);
      setAdlData(response || null);
      setDraftTasks(buildTaskDraftsFromTemplate(response?.draftTemplate || response?.publishedTemplate));
      setEntryDrafts(buildEntryDrafts(response?.publishedTemplate, response?.todayEntry));
      toast.current?.show({ severity: "success", summary: "ADL Tracking Published", detail: "Caregivers can now chart this ADL setup.", life: 2500 });
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message || "Unable to publish ADL tracking.", life: 3500 });
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateFromCarePlan = async () => {
    if (archivedReadOnly) return;
    if (!residentId || !resolvedHomeId) {
      return;
    }
    if (adlData?.hasCarePlan === false) {
      setShowMissingCarePlanDialog(true);
      return;
    }
    setSaving(true);
    try {
      await backendAPIStore.init();
      const response = await backendAPIStore.generateResidentAdlTasksFromCarePlan({
        residentId,
        homeId: resolvedHomeId,
      });
      setSuppressTodayPassAfterReset(Boolean(response?.hideTodayPass));
      setAdlData(response || null);
      toast.current?.show({
        severity: "info",
        summary: "Generation queued",
        detail: "ADL task generation started. The draft will refresh when it is ready.",
        life: 3000,
      });
    } catch (error: any) {
      if (error?.code === "CARE_PLAN_REQUIRED") {
        setShowMissingCarePlanDialog(true);
      } else {
        toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message || "Unable to generate ADL tasks.", life: 3500 });
      }
    } finally {
      setSaving(false);
    }
  };

  const handleResetDraftList = () => {
    if (!residentId || !resolvedHomeId || !hasResettableTasks) {
      return;
    }
    const resetSummaryParts: string[] = [];
    if (draftTasks.length > 0) {
      resetSummaryParts.push(`clear ${draftTasks.length} working ADL task${draftTasks.length === 1 ? "" : "s"} from the ADL List tab`);
    }
    if (entryDrafts.length > 0) {
      resetSummaryParts.push(`remove ${entryDrafts.length} item${entryDrafts.length === 1 ? "" : "s"} from Today's Pass`);
    }
    if (hasPublishedTasks) {
      resetSummaryParts.push("remove the published ADL setup");
    }
    confirmDialog({
      message: renderConfirmMessage(
        "Reset the working ADL task list?",
        `This will ${resetSummaryParts.join(" and ")}.`,
      ),
      header: "Reset ADL Task List",
      icon: "pi pi-info-circle",
      ...appConfirmDialogClasses,
      accept: async () => {
        setSaving(true);
        try {
          await backendAPIStore.init();
          const response = await backendAPIStore.resetResidentAdlDraft({
            residentId,
            homeId: resolvedHomeId,
          });
          setSuppressTodayPassAfterReset(true);
          setAdlData(response || null);
          const refreshedDraftTasks = buildTaskDraftsFromTemplate(response?.draftTemplate || response?.publishedTemplate);
          const refreshedEntryDrafts: any[] = [];
          setDraftTasks(refreshedDraftTasks);
          setEntryDrafts(refreshedEntryDrafts);
          toast.current?.show({
            severity: "success",
            summary: "ADL Task List Reset",
            detail: "The ADL task list, Today's Pass, and published setup were reset.",
            life: 2500,
          });
        } catch (error: any) {
          toast.current?.show({
            severity: "error",
            summary: getErrorTitle(error),
            detail: error?.message || "Unable to reset the ADL list.",
            life: 3500,
          });
        } finally {
          setSaving(false);
        }
      },
      reject: () => { },
    });
  };

  const handleAddCustomTask = () => {
    if (archivedReadOnly) return;
    const label = String(customTask?.label || "").trim();
    if (!label) {
      toast.current?.show({ severity: "warn", summary: "Task Label Required", detail: "Enter a task name before adding it.", life: 2500 });
      return;
    }
    const scheduleDayPattern = normalizeAdlScheduleDayPattern(customTask?.scheduleDayPattern) || "daily";
    const scheduledDays = normalizeAdlScheduleDays(customTask?.scheduledDays);
    const scheduleMonthDays = normalizeAdlScheduleMonthDays(customTask?.scheduleMonthDays);
    const scheduledTimes = String(customTask?.scheduledTimesText || "")
      .split(",")
      .map((value: string) => value.trim())
      .filter(Boolean);
    const effectiveStartDate = String(customTask?.effectiveStartDate || "").trim();
    const effectiveEndDate = String(customTask?.effectiveEndDate || "").trim();
    const discontinued = customTask?.discontinued === true;
    const discontinuedAt = discontinued
      ? String(customTask?.discontinuedAt || getLosAngelesTodayDateString()).trim()
      : "";
    const discontinuedReason = discontinued ? String(customTask?.discontinuedReason || "").trim() : "";
    if (effectiveStartDate && effectiveEndDate && effectiveEndDate < effectiveStartDate) {
      toast.current?.show({
        severity: "warn",
        summary: "Invalid dates",
        detail: "Expiration date cannot be before the start date.",
        life: 3000,
      });
      return;
    }
    if (discontinued && !discontinuedReason) {
      toast.current?.show({ severity: "warn", summary: "Reason required", detail: "Enter a discontinuation reason.", life: 3000 });
      return;
    }
    if (discontinued && discontinuedAt > getLosAngelesTodayDateString()) {
      toast.current?.show({ severity: "warn", summary: "Invalid date", detail: "Discontinued date cannot be in the future.", life: 3000 });
      return;
    }
    if (discontinued && effectiveStartDate && discontinuedAt < effectiveStartDate) {
      toast.current?.show({ severity: "warn", summary: "Invalid date", detail: "Discontinued date cannot be before the start date.", life: 3000 });
      return;
    }
    const nextTasks = editingTaskId
      ? draftTasks.map((task) => (
        task.taskId === editingTaskId
          ? {
            ...task,
            label,
            supportLevel: String(customTask?.supportLevel || "").trim(),
            instructions: String(customTask?.instructions || "").trim(),
            precautions: String(customTask?.precautions || "").trim(),
            chartStyle: String(customTask?.chartStyle || "status_only").trim() || "status_only",
            frequencyType: String(customTask?.frequencyType || "daily").trim() || "daily",
            frequencyPreset: inferAdlFrequencyPreset(customTask),
            timesPerDay: Number.isFinite(Number(customTask?.timesPerDay)) ? Number(customTask?.timesPerDay) : 1,
            effectiveStartDate,
            effectiveEndDate,
            discontinued,
            discontinuedAt,
            discontinuedReason,
            scheduledTimes,
            scheduledDays,
            scheduleDayPattern,
            scheduleMonthDays,
            subTypes: Array.isArray(customTask?.subTypes) ? customTask.subTypes : [],
            chartingParameters: getAdlChartingParametersForCharting(customTask?.chartingParameters),
          }
          : task
      ))
      : [
        ...draftTasks,
        {
          taskId: `${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-${draftTasks.length + 1}`,
          label,
          taskType: "custom",
          supportLevel: String(customTask?.supportLevel || "").trim(),
          instructions: String(customTask?.instructions || "").trim(),
          precautions: String(customTask?.precautions || "").trim(),
          chartStyle: String(customTask?.chartStyle || "status_only").trim() || "status_only",
          frequencyType: String(customTask?.frequencyType || "daily").trim() || "daily",
          frequencyPreset: inferAdlFrequencyPreset(customTask),
          timesPerDay: Number.isFinite(Number(customTask?.timesPerDay)) ? Number(customTask?.timesPerDay) : 1,
          effectiveStartDate,
          effectiveEndDate,
          discontinued,
          discontinuedAt,
          discontinuedReason,
          scheduledTimes,
          scheduledDays,
          scheduleDayPattern,
          scheduleMonthDays,
          subTypes: Array.isArray(customTask?.subTypes) ? customTask.subTypes : [],
          chartingParameters: getAdlChartingParametersForCharting(customTask?.chartingParameters),
          enabled: true,
          source: "manual",
          displayOrder: draftTasks.length,
          statusOptions: defaultStatusOptions.map((option) => option.value),
        },
      ];
    void saveDraftTasks(nextTasks).then((saved) => {
      if (saved) {
        resetCustomTaskDialog();
      }
    });
  };

  const resetCustomTaskDialog = () => {
    setCustomTask({
      label: "",
      supportLevel: "",
      instructions: "",
      precautions: "",
      chartStyle: "status_only",
      frequencyType: "scheduled_times",
      frequencyPreset: "once_daily",
      timesPerDay: 1,
      effectiveStartDate: getLosAngelesTodayDateString(),
      effectiveEndDate: "",
      discontinued: false,
      discontinuedAt: "",
      discontinuedReason: "",
      scheduledTimesText: "08:00",
      scheduleDayPattern: "daily",
      scheduledDays: [],
      scheduleMonthDays: [],
      subTypes: [],
    });
    setSubTypeDraft({ code: "", label: "" });
    setEditingTaskId("");
    setShowCustomTaskDialog(false);
  };

  const openEditTaskDialog = (task: any) => {
    setEditingTaskId(String(task?.taskId || "").trim());
    const taskSource = String(task?.source || "").trim().toLowerCase();
    setCustomTask({
      label: String(task?.label || "").trim(),
      supportLevel: String(task?.supportLevel || "").trim(),
      instructions: String(task?.instructions || "").trim(),
      precautions: String(task?.precautions || "").trim(),
      chartStyle: String(task?.chartStyle || "status_only").trim() || "status_only",
      frequencyType: String(task?.frequencyType || "daily").trim() || "daily",
      frequencyPreset: inferAdlFrequencyPreset(task),
      timesPerDay: Number.isFinite(Number(task?.timesPerDay)) ? Number(task?.timesPerDay) : 1,
      effectiveStartDate: String(task?.effectiveStartDate || (taskSource.startsWith("care_plan") ? getLosAngelesTodayDateString() : "")).trim(),
      effectiveEndDate: String(task?.effectiveEndDate || "").trim(),
      discontinued: task?.discontinued === true,
      discontinuedAt: String(task?.discontinuedAt || "").trim(),
      discontinuedReason: String(task?.discontinuedReason || "").trim(),
      scheduledTimesText: Array.isArray(task?.scheduledTimes) ? task.scheduledTimes.join(", ") : "",
      scheduleDayPattern: normalizeAdlScheduleDayPattern(task?.scheduleDayPattern)
        || (normalizeAdlScheduleMonthDays(task?.scheduleMonthDays).length ? "specific_month_days" : "")
        || (normalizeAdlScheduleDays(task?.scheduledDays).length ? "specific_days" : "daily"),
      scheduledDays: normalizeAdlScheduleDays(task?.scheduledDays),
      scheduleMonthDays: normalizeAdlScheduleMonthDays(task?.scheduleMonthDays),
      subTypes: Array.isArray(task?.subTypes) ? task.subTypes : [],
      chartingParameters: getAdlChartingParametersForCharting(task?.chartingParameters),
    });
    setSubTypeDraft({ code: "", label: "" });
    setShowCustomTaskDialog(true);
  };

  const toggleTaskDeleted = (taskId: string) => {
    setDraftTasks((currentTasks) => currentTasks.map((task) => (
      task.taskId === taskId
        ? {
          ...task,
          enabled: task.enabled === false,
        }
        : task
    )));
  };

  const handleDeleteEditingTask = () => {
    if (archivedReadOnly) return;
    if (!editingTaskId) {
      return;
    }
    const nextTasks = draftTasks.map((task) => (
      task.taskId === editingTaskId
        ? {
          ...task,
          enabled: task.enabled === false,
        }
        : task
    ));
    void saveDraftTasks(nextTasks, "ADL item updated.").then((saved) => {
      if (saved) {
        resetCustomTaskDialog();
      }
    });
  };

  const handleAddSubtypeOption = () => {
    const label = String(subTypeDraft?.label || "").trim();
    if (!label) {
      toast.current?.show({
        severity: "warn",
        summary: "Option Required",
        detail: "Enter an option name before adding it.",
        life: 2500,
      });
      return;
    }
    const code = label.toUpperCase().replace(/[^A-Z0-9]+/g, "_").replace(/^_+|_+$/g, "") || `OPTION_${Date.now()}`;
    setCustomTask((current: any) => ({
      ...current,
      subTypes: [
        ...(Array.isArray(current?.subTypes) ? current.subTypes.filter((item: any) => String(item?.code || "").trim().toUpperCase() !== code) : []),
        {
          subTypeId: `${String(current?.label || "adl").toLowerCase().replace(/[^a-z0-9]+/g, "-") || "adl"}-subtype-${Date.now()}`,
          code,
          label,
          active: true,
          displayOrder: Array.isArray(current?.subTypes) ? current.subTypes.length : 0,
        },
      ],
    }));
    setSubTypeDraft({ code: "", label: "" });
  };

  const removeSubtypeOption = (subTypeId: string) => {
    setCustomTask((current: any) => ({
      ...current,
      subTypes: (Array.isArray(current?.subTypes) ? current.subTypes : []).filter((item: any) => String(item?.subTypeId || "").trim() !== subTypeId),
    }));
  };

  const openChartingParameterDialog = (parameter?: any) => {
    const parameterId = String(parameter?.parameterId || "").trim();
    setEditingChartingParameterId(parameterId);
    setChartingParameterDraft({
      parameterId,
      label: String(parameter?.label || "").trim(),
      type: parameter?.type === "number" ? "number" : "select",
      optionsText: Array.isArray(parameter?.options) ? parameter.options.join(", ") : "",
    });
    setShowChartingParameterDialog(true);
  };

  const saveChartingParameter = () => {
    if (archivedReadOnly) return;
    const label = String(chartingParameterDraft?.label || "").trim();
    if (!label) return;
    const parameterId = editingChartingParameterId || `${String(customTask?.label || "adl").toLowerCase().replace(/[^a-z0-9]+/g, "-")}-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
    const parameter = {
      parameterId,
      label,
      type: chartingParameterDraft?.type === "number" ? "number" : "select",
      options: chartingParameterDraft?.type === "number" ? [] : String(chartingParameterDraft?.optionsText || "").split(",").map((value: string) => value.trim()).filter(Boolean),
    };
    setCustomTask((current: any) => ({
      ...current,
      chartingParameters: [
        ...(Array.isArray(current?.chartingParameters) ? current.chartingParameters.filter((item: any) => String(item?.parameterId || "") !== parameterId) : []),
        parameter,
      ],
    }));
    setShowChartingParameterDialog(false);
  };

  const removeChartingParameter = (parameterId: string) => {
    setCustomTask((current: any) => ({
      ...current,
      chartingParameters: (Array.isArray(current?.chartingParameters) ? current.chartingParameters : []).filter((item: any) => String(item?.parameterId || "") !== parameterId),
    }));
  };

  const updateEntryDraft = (taskId: string, field: string, value: any) => {
    setEntryDrafts((currentEntries) => currentEntries.map((entry) => (
      String(entry?.occurrenceKey || entry?.taskId || "").trim() === taskId
        ? {
          ...entry,
          [field]: value,
        }
        : entry
    )));
  };

  const handleSaveTodayEntry = async (options?: {
    silent?: boolean;
    nextEntries?: any[];
    dateKey?: string;
    sourceDateKey?: string;
    correctionMode?: boolean;
    targetOccurrenceKey?: string;
    expectedVersion?: number;
    correctionReason?: string;
  }) => {
    if (archivedReadOnly) return;
    if (!residentId || !resolvedHomeId) {
      return;
    }
    const nextEntries = Array.isArray(options?.nextEntries) ? options?.nextEntries : entryDrafts;
    const entriesToSave = Array.isArray(nextEntries)
      ? nextEntries.filter((entry: any) => {
        const value = String(entry?.value || "").trim();
        return value && (adlNoteOnlyActionValues.has(value) || String(entry?.status || "").trim());
      })
      : [];
    setSaving(true);
    try {
      await backendAPIStore.init();
      const response = await backendAPIStore.saveResidentAdlEntry({
        residentId,
        homeId: resolvedHomeId,
        dateKey: String(options?.dateKey || adlData?.todayDateKey || "").trim(),
        sourceDateKey: options?.sourceDateKey,
        taskEntries: entriesToSave,
        correctionMode: options?.correctionMode,
        targetOccurrenceKey: options?.targetOccurrenceKey,
        expectedVersion: options?.expectedVersion,
        correctionReason: options?.correctionReason,
      });
      const refreshedEntryDrafts = buildEntryDrafts(response?.publishedTemplate, response?.todayEntry, response?.todayPassItems);
      setSuppressTodayPassAfterReset(false);
      setAdlData(response || null);
      setEntryDrafts(refreshedEntryDrafts);
      if (options?.correctionMode) {
        setHistoryDateKey(String(response?.historyDateKey || options?.dateKey || historyDateKey).trim());
        setHistoryEntryDrafts(buildEntryDrafts(response?.publishedTemplate, response?.historyEntry, response?.historyPassItems));
      }
      if (!options?.silent) {
        toast.current?.show({
          severity: "success",
          summary: options?.correctionMode ? "Past ADL Entry Updated" : "Today's ADL Saved",
          detail: options?.correctionMode ? "The ADL entry was corrected." : "ADL charting was recorded for today.",
          life: 2500,
        });
      }
    } catch (error: any) {
      if (!options?.silent) {
        toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message || "Unable to save today's ADL charting.", life: 3500 });
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadReport = async () => {
    if (!residentId || !resolvedHomeId) {
      return;
    }
    setExportingReport(true);
    try {
      await backendAPIStore.init();
      const response = await backendAPIStore.generateResidentAdlReportDocument({
        residentId,
        homeId: resolvedHomeId,
        reportMonth: selectedReportMonth,
        reportRange: reportRangeMode,
        reportStartDate: reportRangeMode === "custom" ? reportStartDate : undefined,
        reportEndDate: reportRangeMode === "custom" ? reportEndDate : undefined,
        blankReport: blankAdlReport,
      });
      if (response?.documentUrl) {
        openUrlInNewTab(String(response.documentUrl));
      }
      toast.current?.show({
        severity: "success",
        summary: "ADL Report Ready",
        detail: "The ADL report document opened in a new tab.",
        life: 2500,
      });
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message || "Unable to generate ADL report.", life: 3500 });
    } finally {
      setExportingReport(false);
    }
  };

  const generationStatusDetail = (() => {
    const jobStatus = String(latestGenerationJob?.jobStatus || "").trim().toUpperCase();
    if (!jobStatus) {
      return "Use the care plan to build a working ADL setup, then review it before making it live for caregivers.";
    }
    if (jobStatus === "COMPLETED") {
      return `Last generation completed with ${Number(latestGenerationJob?.taskCount || draftTasks.length || 0)} task${Number(latestGenerationJob?.taskCount || draftTasks.length || 0) === 1 ? "" : "s"}.`;
    }
    if (jobStatus === "FAILED") {
      return String(latestGenerationJob?.errorMessage || "The last generation attempt failed. You can try again.");
    }
    return "Generation is running in the background. The working setup will refresh automatically when it finishes.";
  })();

  const generationJobStatus = String(latestGenerationJob?.jobStatus || "").trim();
  const normalizedGenerationJobStatus = generationJobStatus.toUpperCase();
  const generationIsRunning = ["SUBMITTED", "IN_PROGRESS", "RETRYING"].includes(normalizedGenerationJobStatus);
  const generationCompleted = ["COMPLETED", "SUCCEEDED", "SUCCESS"].includes(normalizedGenerationJobStatus);
  const lastGenerationAt = String(
    latestGenerationJob?.generatedAt
    || latestGenerationJob?.completedAt
    || latestGenerationJob?.createdAt
    || "",
  ).trim();

  const handleOpenGenerateTasksDialog = () => {
    if (adlData?.hasCarePlan === false) {
      setShowMissingCarePlanDialog(true);
      return;
    }
    if (!generationCompleted) {
      setShowGenerateTasksDialog(true);
      return;
    }

    const formattedLastGenerationAt = formatLosAngelesDateTime(lastGenerationAt);
    confirmDialog({
      message: renderConfirmMessage(
        "Regenerate ADL tasks?",
        `ADL tasks were generated previously${formattedLastGenerationAt ? ` on ${formattedLastGenerationAt}` : ""}. Do you want to regenerate them?`,
      ),
      header: "Regenerate ADL Tasks",
      icon: "pi pi-exclamation-triangle",
      ...appConfirmDialogClasses,
      acceptLabel: "Regenerate",
      rejectLabel: "Cancel",
      accept: () => {
        setShowGenerateTasksDialog(true);
        void handleGenerateFromCarePlan();
      },
    });
  };

  useEffect(() => {
    if (!processingGeneration && !generationIsRunning) {
      setGenerationElapsedSeconds(0);
      return undefined;
    }

    const createdAt = String(
      latestGenerationJob?.createdAt
      || latestGenerationJob?.startedAt
      || "",
    ).trim();
    const parsedStartTime = createdAt ? new Date(createdAt).getTime() : Number.NaN;
    const startTime = Number.isNaN(parsedStartTime) ? Date.now() : parsedStartTime;
    const updateElapsedTime = () => {
      setGenerationElapsedSeconds(Math.max(0, Math.floor((Date.now() - startTime) / 1000)));
    };

    updateElapsedTime();
    const timerId = window.setInterval(updateElapsedTime, 1000);
    return () => window.clearInterval(timerId);
  }, [generationIsRunning, latestGenerationJob?.createdAt, latestGenerationJob?.startedAt, processingGeneration]);

  const openChartEntryDialog = (rowData: any, presetValue?: string) => {
    const rowDateKey = String(rowData?.dateKey || adlData?.todayDateKey || "").trim();
    const correctionMode = rowData?.correctionMode === true;
    setChartEntryDraft({
      occurrenceKey: String(rowData?.occurrenceKey || rowData?.taskId || "").trim(),
      label: String(rowData?.label || "").trim(),
      occurrenceLabel: String(rowData?.occurrenceLabel || "Daily Pass").trim(),
      effectiveStartDate: String(rowData?.effectiveStartDate || "").trim(),
      effectiveEndDate: String(rowData?.effectiveEndDate || "").trim(),
      actionLabel: adlChartOutcomeOptions.find((option) => option.value === String(presetValue || rowData?.value || "").trim())?.label || "Record",
      chartStyle: String(rowData?.chartStyle || "status_only").trim() || "status_only",
      subTypes: Array.isArray(rowData?.subTypes) ? rowData.subTypes : [],
      statusOptions: Array.isArray(rowData?.statusOptions) ? rowData.statusOptions : defaultStatusOptions.map((option) => option.value),
      value: String(presetValue || rowData?.value || "").trim() || "recorded",
      selectedSubTypeCode: String(rowData?.selectedSubTypeCode || "").trim().toUpperCase(),
      selectedSubTypeCodes: Array.isArray(rowData?.selectedSubTypeCodes) ? rowData.selectedSubTypeCodes : [],
      chartingParameters: getAdlChartingParametersForCharting(rowData?.chartingParameters),
      parameterValues: rowData?.parameterValues && typeof rowData.parameterValues === "object" ? rowData.parameterValues : {},
      status: String(rowData?.status || rowData?.supportLevel || "").trim(),
      note: String(rowData?.note || "").trim(),
      dateKey: rowDateKey,
      sourceDateKey: rowDateKey,
      entryVersion: Number.isFinite(Number(rowData?.entryVersion)) ? Number(rowData.entryVersion) : 0,
      correctionMode,
      correctionReason: String(rowData?.correctionReason || "").trim(),
      chartTimeMode: String(rowData?.chartTimeMode || (correctionMode && rowDateKey !== getLosAngelesTodayDateString() ? "scheduled" : "current")).trim(),
      exactChartTime: String(rowData?.exactChartTime || "").trim(),
    });
    setApplyHospitalizedToAllForDay(false);
    setShowChartEntryDialog(true);
  };

  const closeChartEntryDialog = () => {
    setShowChartEntryDialog(false);
    setChartEntryDraft(null);
  };

  const applyChartEntryDialog = () => {
    const occurrenceKey = String(chartEntryDraft?.occurrenceKey || "").trim();
    if (!occurrenceKey) {
      closeChartEntryDialog();
      return;
    }
    const value = String(chartEntryDraft?.value || "").trim();
    const correctionMode = chartEntryDraft?.correctionMode === true;
    const correctionReason = String(chartEntryDraft?.correctionReason || "").trim();
    const dateKey = String(chartEntryDraft?.dateKey || adlData?.todayDateKey || "").trim();
    const noteOnlyAction = adlNoteOnlyActionValues.has(value);
    const supportRequired = String(chartEntryDraft?.status || "").trim();
    const note = String(chartEntryDraft?.note || "").trim();
    const noteRequired = value !== "recorded";
    const chartingParameters = Array.isArray(chartEntryDraft?.chartingParameters) ? chartEntryDraft.chartingParameters : [];
    const parameterValues = chartEntryDraft?.parameterValues && typeof chartEntryDraft.parameterValues === "object" ? chartEntryDraft.parameterValues : {};
    const missingParameter = chartingParameters.find((parameter: any) => {
      const parameterId = String(parameter?.parameterId || "").trim();
      const parameterValue = parameterValues[parameterId];
      return !parameterId || parameterValue === null || parameterValue === undefined || String(parameterValue).trim() === "";
    });
    if (!dateKey || !value || (correctionMode && !correctionReason) || (!noteOnlyAction && !supportRequired) || (noteRequired && !note) || (!noteOnlyAction && missingParameter) || (!noteOnlyAction && chartEntryDraft?.chartTimeMode === "exact" && !String(chartEntryDraft?.exactChartTime || "").trim())) {
      const detail = !dateKey ? "A chart date is required." : !value ? "Select an action before saving." : correctionMode && !correctionReason ? "Enter a correction reason before saving." : !noteOnlyAction && !supportRequired ? "Select the support required before saving." : noteRequired && !note ? "Note is required for this action." : !noteOnlyAction && missingParameter ? `Select ${String(missingParameter?.label || "all charting parameters")} before saving.` : "Select an exact charting time before saving.";
      toast.current?.show({ severity: "warn", summary: "Required information", detail, life: 3000 });
      return;
    }
    const chartFields = {
      value,
      selectedSubTypeCode: String(chartEntryDraft?.selectedSubTypeCode || "").trim().toUpperCase(),
      selectedSubTypeCodes: Array.isArray(chartEntryDraft?.selectedSubTypeCodes) ? chartEntryDraft.selectedSubTypeCodes : [],
      status: noteOnlyAction ? "" : String(chartEntryDraft?.status || "").trim(),
      note,
      parameterValues: noteOnlyAction ? {} : parameterValues,
      chartTimeMode: noteOnlyAction ? "current" : String(chartEntryDraft?.chartTimeMode || "current").trim(),
      exactChartTime: noteOnlyAction ? "" : String(chartEntryDraft?.exactChartTime || "").trim(),
    };
    const bulkHospitalizedFields = {
      value,
      status: String(chartEntryDraft?.status || "").trim(),
      note,
      chartTimeMode: String(chartEntryDraft?.chartTimeMode || "current").trim(),
      exactChartTime: String(chartEntryDraft?.exactChartTime || "").trim(),
    };
    const sourceEntries = correctionMode ? historyEntryDrafts : entryDrafts;
    const nextEntries = sourceEntries.map((entry) => (
      (applyHospitalizedToAllForDay && value === "hospitalized") || String(entry?.occurrenceKey || entry?.taskId || "").trim() === occurrenceKey
        ? {
          ...entry,
          ...(applyHospitalizedToAllForDay && value === "hospitalized" ? bulkHospitalizedFields : chartFields),
        }
        : entry
    ));
    if (correctionMode) {
      setHistoryEntryDrafts(nextEntries);
    } else {
      setEntryDrafts(nextEntries);
    }
    const updatedEntry = nextEntries.find((entry) => String(entry?.occurrenceKey || entry?.taskId || "").trim() === occurrenceKey);
    const entriesToSave = applyHospitalizedToAllForDay && value === "hospitalized"
      ? nextEntries.filter((entry) => String(entry?.value || "").trim() && (adlNoteOnlyActionValues.has(String(entry?.value || "").trim()) || String(entry?.status || "").trim()))
      : updatedEntry ? [updatedEntry] : [];
    void handleSaveTodayEntry({
      nextEntries: entriesToSave,
      dateKey,
      sourceDateKey: correctionMode ? String(chartEntryDraft?.sourceDateKey || dateKey).trim() : undefined,
      correctionMode,
      targetOccurrenceKey: correctionMode ? occurrenceKey : undefined,
      expectedVersion: correctionMode ? Number(chartEntryDraft?.entryVersion || 0) : undefined,
      correctionReason: correctionMode ? correctionReason : undefined,
    });
    closeChartEntryDialog();
  };

  const draftStatusBody = (rowData: any) => {
    const statusValue = rowData?.enabled === false
      ? "Deleted"
      : rowData?.discontinued === true
        ? "Discontinued"
        : "Active";
    const statusSeverity = rowData?.enabled === false
      ? "secondary"
      : rowData?.discontinued === true
        ? "warning"
        : "success";
    return (
      <Tag value={statusValue} severity={statusSeverity as any} rounded />
    );
  };

  const subTypeSummaryBody = (rowData: any) => {
    const subTypes = Array.isArray(rowData?.subTypes) ? rowData.subTypes : [];
    if (!subTypes.length) {
      return <span style={appMutedTextStyle}>No subtype options</span>;
    }
    return (
      <div className="flex gap-2 flex-wrap">
        {subTypes.map((subType: any) => (
          <Tag
            key={String(subType?.subTypeId || `${subType?.code || ""}-${subType?.label || ""}`)}
            value={`${String(subType?.code || "").trim().toUpperCase()} ${String(subType?.label || "").trim()}`.trim()}
            severity="info"
          />
        ))}
      </div>
    );
  };

  const scheduleSummaryBody = (rowData: any) => {
    const frequencyLabel = adlFrequencyOptions.find((option) => option.value === String(rowData?.frequencyType || "").trim())?.label || "Daily";
    const scheduledTimes = Array.isArray(rowData?.scheduledTimes) ? rowData.scheduledTimes.join(", ") : "";
    const scheduledDays = Array.isArray(rowData?.scheduledDays)
      ? adlWeekdayOptions.filter((option) => normalizeAdlScheduleDays(rowData.scheduledDays).includes(option.value)).map((option) => option.label).join(", ")
      : "";
    const scheduleMonthDays = Array.isArray(rowData?.scheduleMonthDays)
      ? normalizeAdlScheduleMonthDays(rowData.scheduleMonthDays).join(", ")
      : "";
    const schedulePattern = normalizeAdlScheduleDayPattern(rowData?.scheduleDayPattern)
      || (Array.isArray(rowData?.scheduleMonthDays) && rowData.scheduleMonthDays.length ? "specific_month_days" : "")
      || (Array.isArray(rowData?.scheduledDays) && rowData.scheduledDays.length ? "specific_days" : "daily");
    const schedulePatternLabel = adlScheduleDayPatternOptions.find((option) => option.value === schedulePattern)?.label || "Every day";
    return (
      <div style={{ lineHeight: 1.5 }}>
        <div style={{ fontWeight: 700, color: "#0f172a" }}>{frequencyLabel}</div>
        <div style={{ ...appMutedTextStyle, marginTop: "0.2rem" }}>
          {scheduledTimes || scheduledDays || scheduleMonthDays
            ? [schedulePatternLabel, scheduledTimes, schedulePattern === "specific_days" ? scheduledDays : "", schedulePattern === "specific_month_days" ? scheduleMonthDays : ""].filter(Boolean).join(" | ")
            : `${Number(rowData?.timesPerDay || 1)} time${Number(rowData?.timesPerDay || 1) === 1 ? "" : "s"} per day`}
        </div>
      </div>
    );
  };

  const getScheduledOccurrenceCount = (rowData: any) => {
    const scheduledTimesCount = Array.isArray(rowData?.scheduledTimes) ? rowData.scheduledTimes.length : 0;
    const timesPerDayCount = Number(rowData?.timesPerDay || 1);
    return Math.max(scheduledTimesCount, Number.isFinite(timesPerDayCount) ? timesPerDayCount : 1, 1);
  };

  const renderMobileAdlDetail = (rowData: any, detail: "instructions" | "precautions") => {
    const value = String(rowData?.[detail] || "").trim();
    if (!value) return null;

    const taskKey = String(rowData?.taskId || rowData?.adlItemId || rowData?.occurrenceKey || rowData?.label || "adl-task").trim();
    const visibilityKey = `${taskKey}:${detail}`;
    const isVisible = mobileAdlDetails[visibilityKey] === true;
    const label = detail === "precautions" ? "Risks & Precautions" : "Instructions";
    const detailStyle = {
      ...appMutedTextStyle,
      fontSize: "0.82rem",
      whiteSpace: "pre-wrap" as const,
      ...(detail === "precautions" ? { color: "#9a3412" } : {}),
    };

    return (
      <>
        <div className="hidden md:block mt-1" style={detailStyle}>
          <strong style={detail === "instructions" ? { color: "#334155" } : undefined}>{label}:</strong> {value}
        </div>
        <div className="block md:hidden mt-2">
          <Button
            type="button"
            label={`${isVisible ? "Hide" : "Show"} ${label}`}
            size="small"
            severity={detail === "precautions" ? "warning" : "secondary"}
            outlined
            rounded
            onClick={() => setMobileAdlDetails((current) => ({ ...current, [visibilityKey]: !isVisible }))}
          />
          {isVisible ? (
            <div style={{ ...detailStyle, marginTop: "0.5rem" }}>
              <strong style={detail === "instructions" ? { color: "#334155" } : undefined}>{label}:</strong> {value}
            </div>
          ) : null}
        </div>
      </>
    );
  };

  const adlTaskBody = (rowData: any) => {
    const supportLevel = String(rowData?.supportLevel || "").trim();
    const effectiveStartDate = String(rowData?.effectiveStartDate || "").trim();
    const effectiveEndDate = String(rowData?.effectiveEndDate || "").trim();

    return (
      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 700, color: "#0f172a", textDecoration: rowData?.discontinued === true ? "line-through" : undefined }}>
          {rowData?.label || "ADL Task"}
        </div>
        <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.84rem" }}>
          {scheduleSummaryBody(rowData)}
        </div>
        {Array.isArray(rowData?.subTypes) && rowData.subTypes.length ? (
          <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.82rem" }}>
            <strong style={{ color: "#334155" }}>Options:</strong> {rowData.subTypes.map((subType: any) => `${String(subType?.code || "").trim().toUpperCase()} ${String(subType?.label || "").trim()}`.trim()).filter(Boolean).join(", ")}
          </div>
        ) : null}
        {renderMobileAdlDetail(rowData, "instructions")}
        {renderMobileAdlDetail(rowData, "precautions")}
        {(effectiveStartDate || effectiveEndDate) ? (
          <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.82rem" }}>
            {[effectiveStartDate ? `Start: ${effectiveStartDate}` : "", effectiveEndDate ? `Exp: ${effectiveEndDate}` : ""].filter(Boolean).join(" | ")}
          </div>
        ) : null}
        {rowData?.discontinuedReason ? (
          <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.82rem" }}>
            <strong style={{ color: "#334155" }}>Reason:</strong> {String(rowData.discontinuedReason).trim()}
          </div>
        ) : null}
      </div>
    );
  };

  const passTaskBody = (rowData: any) => (
    <div style={{ minWidth: 0 }}>
      <div className="flex align-items-center gap-2 flex-wrap">
        <div style={{ fontWeight: 700, color: "#0f172a", textDecoration: rowData?.discontinued === true ? "line-through" : undefined }}>
          {rowData?.label || "ADL Task"}
        </div>
        <Button
          type="button"
          icon="pi pi-pencil"
          outlined
          rounded
          severity="warning"
          size="small"
          style={{ color: "#ea580c", background: "#fff7ed" }}
          tooltip="Edit"
          tooltipOptions={{ position: "top" }}
          aria-label={`Edit ${rowData?.label || "ADL task"}`}
          onClick={() => openEditTaskDialog(rowData)}
        />
      </div>
      <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.84rem" }}>
        {String(rowData?.occurrenceLabel || "").trim() && String(rowData?.occurrenceLabel || "").trim() !== "Daily Pass"
          ? String(rowData.occurrenceLabel).trim()
          : scheduleSummaryBody(rowData)}
      </div>
      {rowData?.supportLevel ? (
        <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.82rem", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          <strong style={{ color: "#334155" }}>Support:</strong> {rowData.supportLevel}
        </div>
      ) : null}
      {renderMobileAdlDetail(rowData, "instructions")}
      {renderMobileAdlDetail(rowData, "precautions")}
    </div>
  );

  const formatAdlOccurrenceLabel = (rowData: any) => {
    const label = String(rowData?.occurrenceLabel || "").trim();
    const match = label.match(/^(\d{1,2}):(\d{2})(?:\s*([ap]m))?$/i);
    if (!match) return label || "Scheduled";
    let hours = Number(match[1]);
    const minutes = Number(match[2]);
    const meridiem = String(match[3] || "").toLowerCase();
    if (meridiem === "pm" && hours < 12) hours += 12;
    if (meridiem === "am" && hours === 12) hours = 0;
    if (hours > 23 || minutes > 59) return label;
    return `${hours % 12 || 12}:${String(minutes).padStart(2, "0")} ${hours < 12 ? "AM" : "PM"}`;
  };

  const listStatusBody = (rowData: any) => (
    <div style={{ minWidth: 0 }}>
      {draftStatusBody(rowData)}
      {rowData?.discontinuedAt ? (
        <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.82rem" }}>
          {String(rowData.discontinuedAt)}
        </div>
      ) : null}
    </div>
  );

  const parametersBody = (rowData: any) => {
    const parameters = getAdlChartingParametersForCharting(rowData?.chartingParameters);
    if (!parameters.length) return <span style={appMutedTextStyle}>None</span>;
    return (
      <div className="flex flex-column gap-1">
        {parameters.map((parameter: any) => (
          <div key={String(parameter?.parameterId || parameter?.label || "parameter")} style={{ fontSize: "0.82rem" }}>
            <strong style={{ color: "#334155" }}>{parameter?.label || "Parameter"}:</strong>{" "}
            {parameter?.type === "number" ? "Enter a number" : (Array.isArray(parameter?.options) ? parameter.options.join(", ") : "Single select")}
          </div>
        ))}
      </div>
    );
  };

  const getTodayStatusMeta = (rowData: any) => {
    const chartOutcome = String(rowData?.value || "").trim().toLowerCase();
    const statusSummary = String(rowData?.status || "").trim();
    const scheduledLabel = String(rowData?.occurrenceLabel || "").trim();
    const scheduledTimeMatch = scheduledLabel.match(/^(\d{1,2}):(\d{2})(?:\s*([ap]m))?$/i);
    let scheduledMinutes = -1;
    if (scheduledTimeMatch) {
      let hours = Number(scheduledTimeMatch[1]);
      const minutes = Number(scheduledTimeMatch[2]);
      const meridiem = String(scheduledTimeMatch[3] || "").toLowerCase();
      if (meridiem === "pm" && hours < 12) hours += 12;
      if (meridiem === "am" && hours === 12) hours = 0;
      if (hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59) scheduledMinutes = (hours * 60) + minutes;
    }
    const todayDateKey = String(adlData?.todayDateKey || "").trim();
    const currentParts = new Intl.DateTimeFormat("en-US", {
      timeZone: "America/Los_Angeles",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    }).formatToParts(new Date());
    const currentDateKey = `${currentParts.find((part) => part.type === "year")?.value}-${currentParts.find((part) => part.type === "month")?.value}-${currentParts.find((part) => part.type === "day")?.value}`;
    const currentMinutes = (Number(currentParts.find((part) => part.type === "hour")?.value || 0) * 60)
      + Number(currentParts.find((part) => part.type === "minute")?.value || 0);
    const overdueFallback = !statusSummary && !chartOutcome && scheduledMinutes >= 0
      && todayDateKey === currentDateKey && currentMinutes >= scheduledMinutes;
    const isMissed = rowData?.missed === true || overdueFallback || statusSummary.toLowerCase() === "missed" || chartOutcome === "missed";
    if (isMissed) return { label: "Missed", severity: "danger" };
    if (chartOutcome === "recorded") return { label: "Recorded", severity: "success" };
    if (chartOutcome === "not_recorded") return { label: "Not recorded", severity: "warning" };
    if (chartOutcome === "refused") return { label: "Refused", severity: "danger" };
    if (chartOutcome === "family") return { label: "Family", severity: "info" };
    if (chartOutcome === "hospitalized") return { label: "Hospitalized", severity: "warning" };
    return { label: "Pending", severity: "secondary" };
  };

  const todayChartSummaryCardBody = (rowData: any) => {
    const statusMeta = getTodayStatusMeta(rowData);
    const subtypeSummary = buildSubtypeSelectionSummary(rowData);
    const chartingParameters = getAdlChartingParametersForCharting(rowData?.chartingParameters);
    const parameterValues = rowData?.parameterValues && typeof rowData.parameterValues === "object"
      ? rowData.parameterValues
      : {};
    const parameterSummary = chartingParameters
      .map((parameter: any) => {
        const parameterId = String(parameter?.parameterId || "").trim();
        const value = parameterValues[parameterId];
        if (value === null || value === undefined || String(value).trim() === "") {
          return "";
        }
        return `${String(parameter?.label || parameterId || "Parameter").trim()}: ${String(value).trim()}`;
      })
      .filter(Boolean)
      .join(" | ");
    const supportSummary = String(rowData?.status || "").trim();
    const noteSummary = String(rowData?.note || "").trim();
    const statusMessage = String(rowData?.statusMessage || "").trim()
      || (statusMeta.label === "Missed" ? "No charted entry was saved for this scheduled ADL pass." : "");
    return (
      <div style={{ minWidth: 0, border: "1px solid #e2e8f0", borderRadius: "12px", padding: "0.65rem 0.75rem", background: "#fff" }}>
        <div className="flex justify-content-between align-items-center gap-2 flex-wrap">
          <Tag value={`${statusMeta.label} ${formatAdlOccurrenceLabel(rowData)}`} severity={statusMeta.severity as any} rounded />
        </div>
        {statusMessage ? (
          <div className="mt-2" style={{ ...appMutedTextStyle, fontSize: "0.82rem", color: statusMeta.label === "Missed" ? "#b91c1c" : undefined }}>
            {statusMessage}
          </div>
        ) : null}
        {supportSummary ? (
          <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.82rem" }}>
            Support required: {supportSummary}
          </div>
        ) : null}
        {subtypeSummary ? (
          <div className="mt-2" style={{ ...appMutedTextStyle, fontSize: "0.82rem" }}>
            {subtypeSummary}
          </div>
        ) : null}
        {parameterSummary ? (
          <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.82rem" }}>
            {parameterSummary}
          </div>
        ) : null}
        {noteSummary ? (
          <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.82rem", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            Note: {noteSummary}
          </div>
        ) : null}
      </div>
    );
  };

  const todayChartSummaryBody = (rowData: any) => {
    const occurrences = Array.isArray(rowData?.occurrences) ? rowData.occurrences : [rowData];
    return (
      <div className="flex flex-column gap-2" style={{ minWidth: "17rem" }}>
        {occurrences.map((occurrence: any) => (
          <div key={String(occurrence?.occurrenceKey || occurrence?.taskId || "adl-occurrence")}>
            {todayChartSummaryCardBody(occurrence)}
          </div>
        ))}
      </div>
    );
  };

  const buildSubtypeSelectionSummary = (rowData: any) => {
    const selectedCodes = Array.isArray(rowData?.selectedSubTypeCodes) && rowData.selectedSubTypeCodes.length
      ? rowData.selectedSubTypeCodes
      : (String(rowData?.selectedSubTypeCode || "").trim() ? [String(rowData?.selectedSubTypeCode || "").trim()] : []);
    if (!selectedCodes.length) {
      return "";
    }
    const subTypes = Array.isArray(rowData?.subTypes) ? rowData.subTypes : [];
    return selectedCodes.map((code: string) => {
      const matched = subTypes.find((subType: any) => String(subType?.code || "").trim().toUpperCase() === String(code || "").trim().toUpperCase());
      return matched ? `${matched.code} ${matched.label}`.trim() : String(code || "").trim().toUpperCase();
    }).filter(Boolean).join(", ");
  };

  const draftActionsBody = (rowData: any) => (
    <div className="flex gap-2 flex-wrap">
      <Button
        type="button"
        icon="pi pi-pencil"
        size="small"
        label="Edit"
        rounded
        outlined
        severity="warning"
        style={{ color: "#ea580c", background: "#fff7ed" }}
        aria-label={`Edit ${rowData?.label || "ADL task"}`}
        onClick={() => openEditTaskDialog(rowData)}
      />
    </div>
  );

  const todayChartActionCardBody = (rowData: any) => {
    const currentValue = String(rowData?.value || "").trim().toLowerCase();
    const actionTags = [
      { label: "Record", value: "recorded", selected: { background: "#dcfce7", borderColor: "#16a34a", color: "#166534" }, idle: { background: "#fff", borderColor: "#bbf7d0", color: "#166534" } },
      { label: "Not Recorded", value: "not_recorded", selected: { background: "#fee2e2", borderColor: "#dc2626", color: "#991b1b" }, idle: { background: "#fff", borderColor: "#fecaca", color: "#991b1b" } },
      { label: "Refused", value: "refused", selected: { background: "#fee2e2", borderColor: "#b91c1c", color: "#991b1b" }, idle: { background: "#fff", borderColor: "#fca5a5", color: "#991b1b" } },
      { label: "Family", value: "family", selected: { background: "#dbeafe", borderColor: "#2563eb", color: "#1d4ed8" }, idle: { background: "#fff", borderColor: "#bfdbfe", color: "#1d4ed8" } },
      { label: "Hospitalized", value: "hospitalized", selected: { background: "#fef3c7", borderColor: "#d97706", color: "#9a3412" }, idle: { background: "#fff", borderColor: "#fcd34d", color: "#9a3412" } },
    ];
    const scheduledInFuture = isAdlOccurrenceUnavailableBeforeWindow(rowData);
    const unrestrictedActionValues = new Set(["family", "hospitalized"]);
    const visibleActionTags = currentValue
      ? actionTags.filter((action) => action.value === currentValue)
      : scheduledInFuture
        ? actionTags.filter((action) => unrestrictedActionValues.has(action.value))
        : actionTags;
    const applyQuickChartAction = (value: string) => {
      openChartEntryDialog(rowData, value);
    };
    return (
      <div style={{ minWidth: "14rem", border: "1px solid #e2e8f0", borderRadius: "12px", padding: "0.65rem 0.75rem", background: "#fff" }}>
        <div className="mb-2" style={{ fontSize: "0.8rem", fontWeight: 700, color: "#475569" }}>
          {formatAdlOccurrenceLabel(rowData)}
        </div>
        {scheduledInFuture ? (
          <div className="mb-2" style={{ border: "1px solid #fde68a", background: "#fffbeb", borderRadius: "12px", padding: "0.7rem 0.8rem", color: "#92400e", fontSize: "0.82rem", lineHeight: 1.45 }}>
            Record is available 1 hour before the scheduled tracking time. Family and Hospitalized can be recorded at any time.
          </div>
        ) : null}
        <div className="flex gap-2 flex-wrap">
          {visibleActionTags.map((action) => (
            <Tag
              key={action.value}
              value={action.label}
              className="adl-action-tag"
              rounded
              style={{ border: "1px solid", borderRadius: "999px", fontSize: "0.78rem", fontWeight: 700, padding: "0.35rem 0.65rem", whiteSpace: "nowrap", borderColor: (currentValue === action.value ? action.selected : action.idle).borderColor, background: (currentValue === action.value ? action.selected : action.idle).background, color: (currentValue === action.value ? action.selected : action.idle).color, cursor: "pointer", opacity: currentValue && currentValue !== action.value ? 0.62 : 1, boxShadow: currentValue === action.value ? "0 0 0 2px #0f172a" : undefined }}
              role="button"
              tabIndex={0}
              onClick={() => applyQuickChartAction(action.value)}
              onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") applyQuickChartAction(action.value); }}
            />
          ))}
        </div>
      </div>
    );
  };

  const todayChartActionBody = (rowData: any) => {
    const occurrences = Array.isArray(rowData?.occurrences) ? rowData.occurrences : [rowData];
    return (
      <div className="flex flex-column gap-2" style={{ minWidth: "18rem" }}>
        {occurrences.map((occurrence: any) => (
          <div key={String(occurrence?.occurrenceKey || occurrence?.taskId || "adl-occurrence")}>
            {todayChartActionCardBody(occurrence)}
          </div>
        ))}
      </div>
    );
  };

  const historyChartActionBody = (rowData: any) => {
    const historyRow = {
      ...rowData,
      correctionMode: true,
      dateKey: String(rowData?.dateKey || historyDateKey).trim(),
    };
    const isCharted = !rowData?.syntheticPending
      && (!!String(rowData?.value || "").trim() || !!String(rowData?.status || "").trim());
    return (
      <div className="flex gap-2 flex-wrap" style={{ minWidth: "10rem" }}>
        <Button
          type="button"
          label={isCharted ? "Fix" : "Chart"}
          icon={isCharted ? "pi pi-pencil" : "pi pi-check-circle"}
          size="small"
          rounded
          outlined
          severity={isCharted ? "warning" : "success"}
          onClick={() => openChartEntryDialog(historyRow)}
        />
        {isCharted ? (
          <Button
            type="button"
            label="Delete"
            icon="pi pi-trash"
            size="small"
            rounded
            outlined
            severity="danger"
            onClick={() => setAdlEntryToDelete(historyRow)}
          />
        ) : null}
      </div>
    );
  };

  const markAdlEntryDeleted = async () => {
    if (!residentId || !resolvedHomeId || !adlEntryToDelete?.occurrenceKey || deletingAdlEntry) {
      return;
    }
    setDeletingAdlEntry(true);
    try {
      await backendAPIStore.saveResidentAdlEntry({
        residentId,
        homeId: resolvedHomeId,
        dateKey: String(adlEntryToDelete?.dateKey || historyDateKey).trim(),
        sourceDateKey: String(adlEntryToDelete?.dateKey || historyDateKey).trim(),
        targetOccurrenceKey: String(adlEntryToDelete.occurrenceKey).trim(),
        expectedVersion: Number(adlEntryToDelete?.entryVersion || 0),
        correctionMode: true,
        action: "delete",
        taskEntries: [],
      });
      setAdlEntryToDelete(null);
      await loadAdlHistory(historyDateKey);
      toast.current?.show({
        severity: "success",
        summary: "ADL entry marked deleted",
        detail: "Report will exclude this entry.",
        life: 3000,
      });
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: getErrorTitle(error),
        detail: error?.message || "Unable to mark this ADL entry deleted.",
        life: 3500,
      });
    } finally {
      setDeletingAdlEntry(false);
    }
  };

  const openAdlHistoryDialog = (rowData: any) => {
    setSelectedAdlHistoryEntry(rowData);
    setAdlHistoryDialogVisible(true);
  };

  const historyTaskBody = (rowData: any) => (
    <div style={{ minWidth: 0 }}>
      {passTaskBody(rowData)}
      {String(rowData?.recordedAt || "").trim() ? (
        <div className="mt-2" style={{ ...appMutedTextStyle, fontSize: "0.82rem" }}>
          Recorded at: {formatLosAngelesDateTime(rowData.recordedAt) || rowData.recordedAt}
        </div>
      ) : null}
      {Array.isArray(rowData?.statusHistory) && rowData.statusHistory.length > 0 ? (
        <Button
          type="button"
          className="mt-2"
          label={`${rowData.statusHistory.length} chart events`}
          icon="pi pi-history"
          size="small"
          text
          onClick={() => openAdlHistoryDialog(rowData)}
        />
      ) : null}
      {rowData?.discontinuedReason ? (
        <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.82rem" }}>
          {String(rowData.discontinuedReason).trim()}
        </div>
      ) : null}
    </div>
  );

  if (userRoleType !== ROLE_TYPE_PROVIDER && !isAdminUser) {
    return <Unauthorized />;
  }

  return (
    <div className="container-fluid p-3 provider-resident-adl-page">
      <Toast ref={toast} />
      <style>
        {`
          .provider-resident-adl-page .resident-workspace-tabs .p-tabview-nav-container {
            margin-bottom: 1rem;
          }

          .provider-resident-adl-page .resident-workspace-tabs {
            width: 100%;
          }

          .provider-resident-adl-page .resident-workspace-tabs .p-tabview-nav {
            display: flex;
            flex-wrap: wrap;
            justify-content: center;
            gap: 0.55rem;
            padding: 0.45rem;
            border: 1px solid #dbe4f0;
            border-radius: 18px;
            background: linear-gradient(180deg, #f8fbff 0%, #f1f5f9 100%);
            box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.8);
            width: 100%;
          }

          .provider-resident-adl-page .resident-workspace-tabs .p-tabview-nav li {
            margin: 0;
          }

          .provider-resident-adl-page .resident-workspace-tabs .p-tabview-nav li .p-tabview-nav-link {
            border: 1px solid #d7e0ea;
            border-radius: 14px;
            background: #ffffff;
            color: #475569;
            padding: 0.8rem 1rem;
            font-weight: 700;
            transition: all 0.18s ease;
            box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
          }

          .provider-resident-adl-page .resident-workspace-tabs .p-tabview-nav li.p-highlight .p-tabview-nav-link {
            border-color: #fdba74;
            background: linear-gradient(135deg, #fff7ed 0%, #ffedd5 55%, #fef3c7 100%);
            color: #0f172a;
            box-shadow: 0 8px 18px rgba(249, 115, 22, 0.14);
          }

          .provider-resident-adl-page .resident-workspace-tabs .p-tabview-panels {
            padding: 0;
            background: transparent;
          }

          @media (max-width: 640px) {
            .provider-resident-adl-page .resident-workspace-tabs .p-tabview-nav-container {
              overflow: visible;
            }

            .provider-resident-adl-page .resident-workspace-tabs .p-tabview-nav {
              gap: 0.4rem;
            }

            .provider-resident-adl-page .resident-workspace-tabs .p-tabview-nav li {
              flex: 1 1 calc(50% - 0.25rem);
              min-width: 0;
            }

            .provider-resident-adl-page .resident-workspace-tabs .p-tabview-nav li .p-tabview-nav-link {
              width: 100%;
              min-height: 2.75rem;
              padding: 0.6rem 0.65rem;
              white-space: normal;
              text-align: center;
              line-height: 1.25;
              justify-content: center;
            }
          }

          .provider-resident-adl-page .adl-action-tag .p-tag-value {
            color: inherit !important;
            font-size: inherit !important;
            font-weight: inherit !important;
          }
          .provider-resident-adl-page .chart-dialog-action .p-button-label {
            color: inherit !important;
          }
          .provider-resident-adl-page .chart-dialog-action {
            color: #334155 !important;
          }
          .provider-resident-adl-page .p-selectbutton .p-button {
            color: #334155 !important;
          }
          .provider-resident-adl-page .p-selectbutton .p-button.p-highlight {
            color: #0f172a !important;
          }
        `}
      </style>
      <Dialog
        visible={adlTaskHistoryDialogVisible}
        onHide={() => setAdlTaskHistoryDialogVisible(false)}
        header="ADL Task Configuration History"
        modal
        style={{ width: "min(94vw, 56rem)" }}
      >
        <DataTable
          value={[...(Array.isArray(adlData?.draftTemplate?.taskHistory) ? adlData.draftTemplate.taskHistory : [])].reverse()}
          size="small"
          responsiveLayout="scroll"
          emptyMessage="No task configuration changes recorded."
        >
          <Column field="eventType" header="Change" />
          <Column field="taskLabel" header="Task" />
          <Column header="Dates / Status" body={(row: any) => {
            const updated = row?.updated || {};
            return [updated?.effectiveStartDate ? `Start: ${updated.effectiveStartDate}` : "", updated?.discontinuedAt ? `Discontinued: ${updated.discontinuedAt}` : "", updated?.discontinuedReason ? `Reason: ${updated.discontinuedReason}` : ""].filter(Boolean).join(" | ") || "—";
          }} />
          <Column header="Changed" body={(row: any) => `${row?.changedBy || "Unknown"} · ${formatLosAngelesDateTime(row?.changedAt) || row?.changedAt || ""}`} />
        </DataTable>
      </Dialog>
      <Dialog
        visible={showChartEntryDialog}
        style={adlChartDialogStyle}
        modal
        onHide={closeChartEntryDialog}
        header={renderSurfaceHeader(
          chartEntryDraft?.actionLabel ? `${chartEntryDraft.actionLabel} ADL` : "Chart ADL",
          adlNoteOnlyActionValues.has(String(chartEntryDraft?.value || "").trim())
            ? "Add a note for this action."
            : chartEntryDraft?.correctionMode === true
              ? "Update the support, parameters, and notes for this past pass."
              : "Add support, parameters, notes, and chart time for this pass.",
          "pi pi-file-edit",
        )}
        footer={renderDialogFooter({ loading: saving, onCancel: closeChartEntryDialog, onSubmit: applyChartEntryDialog, submitLabel: "Save", cancelButtonProps: { className: "chart-dialog-action" }, submitButtonProps: { className: "chart-dialog-action" } })}
      >
        <div style={{ display: "grid", gap: "0.95rem" }}>
          {chartEntryDraft?.effectiveStartDate ? (
            <div className="flex align-items-center gap-2" style={{ border: "1px solid #dbeafe", background: "#f8fbff", borderRadius: "10px", padding: "0.7rem 0.8rem", color: "#334155", fontSize: "0.88rem" }}>
              <i className="pi pi-calendar" />
              <span><strong>Start date:</strong> {chartEntryDraft.effectiveStartDate}</span>
            </div>
          ) : null}
          {chartEntryDraft?.correctionMode === true ? (
            <div style={{ border: "1px solid #fde68a", background: "#fffbeb", borderRadius: "12px", padding: "0.85rem" }}>
              <div className="grid">
                <div className="col-12 md:col-6">
                  <label htmlFor="adl-correction-date" className="block mb-2 font-semibold">Date *</label>
                  <Calendar
                    id="adl-correction-date"
                    value={getLosAngelesDateOnlyCalendarValue(chartEntryDraft?.dateKey)}
                    minDate={historyMinDateValue || undefined}
                    maxDate={historyMaxDateValue || undefined}
                    dateFormat="mm/dd/yy"
                    showIcon
                    readOnlyInput
                    onChange={(event) => {
                      const nextDateKey = formatLosAngelesDateOnlyFromCalendar(event.value as Date | null);
                      if (nextDateKey) {
                        setChartEntryDraft((current: any) => ({ ...(current || {}), dateKey: nextDateKey }));
                      }
                    }}
                    className="w-full"
                  />
                </div>
                <div className="col-12 md:col-6">
                  <label htmlFor="adl-correction-status" className="block mb-2 font-semibold">ADL status *</label>
                  <Dropdown
                    id="adl-correction-status"
                    value={chartEntryDraft?.value || ""}
                    options={adlChartOutcomeOptions}
                    optionLabel="label"
                    optionValue="value"
                    onChange={(event) => setChartEntryDraft((current: any) => ({
                      ...(current || {}),
                      value: String(event.value || ""),
                      actionLabel: adlChartOutcomeOptions.find((option) => option.value === event.value)?.label || "Record",
                    }))}
                    className="w-full"
                  />
                </div>
                <div className="col-12">
                  <label htmlFor="adl-correction-reason" className="block mb-2 font-semibold">Correction reason *</label>
                  <InputTextarea
                    id="adl-correction-reason"
                    value={chartEntryDraft?.correctionReason || ""}
                    rows={3}
                    maxLength={500}
                    autoResize
                    placeholder="Explain why this past entry is being corrected."
                    className="w-full"
                    onChange={(event) => setChartEntryDraft((current: any) => ({ ...(current || {}), correctionReason: event.target.value }))}
                  />
                </div>
              </div>
            </div>
          ) : null}
          {!adlNoteOnlyActionValues.has(String(chartEntryDraft?.value || "").trim()) && Array.isArray(chartEntryDraft?.subTypes) && chartEntryDraft.subTypes.length ? (
            <div style={{ order: 2 }}>
              <label className="block mb-2 font-semibold">Sub Status</label>
              <Dropdown
                value={chartEntryDraft?.selectedSubTypeCode || ""}
                options={chartEntryDraft.subTypes.map((subType: any) => ({
                  label: `${String(subType?.code || "").trim().toUpperCase()} ${String(subType?.label || "").trim()}`.trim(),
                  value: String(subType?.code || "").trim().toUpperCase(),
                }))}
                placeholder="Select sub status"
                className="w-full"
                onChange={(event) => setChartEntryDraft((current: any) => ({
                  ...(current || {}),
                  selectedSubTypeCode: String(event.value || ""),
                  selectedSubTypeCodes: event.value ? [String(event.value || "")] : [],
                }))}
              />
            </div>
          ) : null}
          {chartEntryDraft?.value === "hospitalized" && chartEntryDraft?.correctionMode !== true ? (
            <div className="flex align-items-center gap-2" style={{ order: 2 }}>
              <Checkbox
                inputId="adlHospitalizedAllPasses"
                checked={applyHospitalizedToAllForDay}
                onChange={(event) => setApplyHospitalizedToAllForDay(!!event.checked)}
              />
              <label htmlFor="adlHospitalizedAllPasses">Apply Hospitalized to all scheduled passes today</label>
            </div>
          ) : null}
          {!adlNoteOnlyActionValues.has(String(chartEntryDraft?.value || "").trim()) && isAdlChartOutsideOneHourWindow(chartEntryDraft) ? (
            <div style={{ order: 2 }}>
              <div style={{ border: "1px solid #dbeafe", background: "#f8fbff", borderRadius: "16px", padding: "0.85rem 0.95rem" }}>
                <div style={{ fontWeight: 700, color: "#0f172a" }}>Choose administration time</div>
                <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.82rem" }}>
                  Choose whether to save it at the scheduled time, the current time, or an exact charting time.
                </div>
                <div className="mt-2" style={{ border: "1px solid #fde68a", background: "#fffbeb", borderRadius: "12px", padding: "0.8rem 0.9rem", color: "#92400e", fontSize: "0.88rem", lineHeight: 1.5 }}>
                  This ADL item is being charted about {getAdlChartTimingDifferenceHours(chartEntryDraft).toFixed(1)} hours after the scheduled time. Choose whether to save it at the scheduled time or the current time.
                </div>
                <div className="mt-2 grid">
                  {[
                    { value: "scheduled", label: "Recorded at scheduled time", helper: formatAdlOccurrenceLabel(chartEntryDraft) },
                    { value: "current", label: "Recorded at current time", helper: new Intl.DateTimeFormat("en-US", { timeZone: "America/Los_Angeles", hour: "numeric", minute: "2-digit" }).format(new Date()) },
                    { value: "exact", label: "Exact time", helper: "Choose a specific charting time below." },
                  ].map((option) => {
                    const selected = chartEntryDraft?.chartTimeMode === option.value;
                    return (
                      <div className="col-12 md:col-6" key={option.value}>
                        <button type="button" disabled={option.value === "scheduled" && isAdlOccurrenceScheduledInFuture(chartEntryDraft)} onClick={() => setChartEntryDraft((current: any) => ({ ...(current || {}), chartTimeMode: option.value }))} style={{ width: "100%", textAlign: "left", border: `1px solid ${selected ? "#2563eb" : "#dbe3ef"}`, borderRadius: "14px", padding: "0.8rem 0.9rem", background: option.value === "scheduled" && isAdlOccurrenceScheduledInFuture(chartEntryDraft) ? "#f8fafc" : selected ? "#eff6ff" : "#ffffff", color: option.value === "scheduled" && isAdlOccurrenceScheduledInFuture(chartEntryDraft) ? "#94a3b8" : "#0f172a", cursor: option.value === "scheduled" && isAdlOccurrenceScheduledInFuture(chartEntryDraft) ? "not-allowed" : "pointer" }}>
                          <div style={{ fontWeight: 700 }}>{option.label}</div>
                          <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.82rem" }}>{option.helper}</div>
                        </button>
                      </div>
                    );
                  })}
                  <div className="col-12 md:col-6">
                    <div style={{ padding: 0 }}>
                      <label className="block mb-2 font-medium">Exact charting time</label>
                      <Dropdown
                        value={chartEntryDraft?.exactChartTime || ""}
                        options={adlScheduleTimeOptions.filter((timeOption: any) => !isAdlOccurrenceScheduledInFuture({ occurrenceLabel: timeOption.value }))}
                        optionLabel="label"
                        optionValue="value"
                        placeholder="Select exact time"
                        disabled={chartEntryDraft?.chartTimeMode !== "exact"}
                        className="w-full"
                        style={{ width: "100%" }}
                        onChange={(event) => setChartEntryDraft((current: any) => ({ ...(current || {}), exactChartTime: String(event.value || "") }))}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : null}
          {!adlNoteOnlyActionValues.has(String(chartEntryDraft?.value || "").trim()) ? <div style={{ ...appInnerCardStyle, padding: "1rem", order: 1 }}>
            <div className="mb-3" style={{ fontWeight: 800, color: "#0f172a" }}>Charting parameters</div>
            <div className="grid align-items-start">
              <div className="col-12 md:col-6">
                <label className="block mb-2 font-semibold">Support required *</label>
                <Dropdown
                  value={chartEntryDraft?.status || ""}
                  options={supportLevelOptions}
                  placeholder="Select support required"
                  className="w-full"
                  onChange={(event) => setChartEntryDraft((current: any) => ({ ...(current || {}), status: String(event.value || "") }))}
                />
              </div>
              {Array.isArray(chartEntryDraft?.chartingParameters) && chartEntryDraft.chartingParameters.length ? chartEntryDraft.chartingParameters.map((parameter: any) => {
                const parameterId = String(parameter?.parameterId || "").trim();
                const parameterValue = chartEntryDraft?.parameterValues?.[parameterId] ?? null;
                return (
                  <div className="col-12 md:col-6" key={parameterId}>
                    <label className="block mb-2">{parameter.label}</label>
                    {parameter.type === "number" ? (
                      <InputNumber
                        value={typeof parameterValue === "number" ? parameterValue : parameterValue ? Number(parameterValue) : null}
                        className="w-full"
                        onValueChange={(event) => setChartEntryDraft((current: any) => ({ ...(current || {}), parameterValues: { ...(current?.parameterValues || {}), [parameterId]: event.value ?? "" } }))}
                      />
                    ) : (
                      <Dropdown
                        value={parameterValue || ""}
                        options={(Array.isArray(parameter?.options) ? parameter.options : []).map((option: string) => ({ label: option, value: option }))}
                        placeholder="Select one"
                        className="w-full"
                        onChange={(event) => setChartEntryDraft((current: any) => ({ ...(current || {}), parameterValues: { ...(current?.parameterValues || {}), [parameterId]: event.value || "" } }))}
                      />
                    )}
                  </div>
                );
              }) : <div className="col-12" style={appMutedTextStyle}>No additional parameters configured for this ADL item.</div>}
            </div>
          </div> : null}
          <div style={{ order: 3 }}>
            <label className="block mb-2 font-semibold">Note{chartEntryDraft?.value !== "recorded" ? " *" : ""}</label>
            <InputTextarea
              value={chartEntryDraft?.note || ""}
              onChange={(event) => setChartEntryDraft((current: any) => ({ ...(current || {}), note: event.target.value }))}
              rows={4}
              autoResize
              className="w-full"
            />
          </div>
        </div>
      </Dialog>
      <Dialog
        visible={showChartingParameterDialog}
        onHide={() => setShowChartingParameterDialog(false)}
        header={editingChartingParameterId ? "Edit charting parameter" : "Add charting parameter"}
        style={{ width: "34rem", maxWidth: "96vw" }}
        footer={(
          <div className="flex justify-content-end gap-2">
            <Button type="button" label="Cancel" rounded outlined severity="secondary" onClick={() => setShowChartingParameterDialog(false)} />
            <Button type="button" label="Save parameter" rounded severity="warning" onClick={saveChartingParameter} />
          </div>
        )}
      >
        <div className="grid">
          <div className="col-12">
            <label htmlFor="adlParameterLabel" className="block mb-2 font-semibold">Parameter name</label>
            <InputText id="adlParameterLabel" value={chartingParameterDraft.label} onChange={(event) => setChartingParameterDraft((current: any) => ({ ...current, label: event.target.value }))} className="w-full" placeholder="Example: Amount eaten" />
          </div>
          <div className="col-12">
            <label htmlFor="adlParameterType" className="block mb-2 font-semibold">Parameter type</label>
            <Dropdown id="adlParameterType" value={chartingParameterDraft.type} options={[{ label: "Choose from a list", value: "select" }, { label: "Enter a number", value: "number" }]} onChange={(event) => setChartingParameterDraft((current: any) => ({ ...current, type: event.value }))} className="w-full" />
            <div className="mt-2" style={{ color: "#64748b", fontSize: "0.82rem", lineHeight: 1.5 }}>
              {chartingParameterDraft.type === "number"
                ? "Example: 0, 1, 2, or 3 for a count such as bladder episodes."
                : "Example: 0%, 25%, 50%, or 100%; or choices such as Small, Medium, and Large."}
            </div>
          </div>
          {chartingParameterDraft.type !== "number" ? (
            <div className="col-12">
              <label htmlFor="adlParameterOptions" className="block mb-2 font-semibold">Options to choose from</label>
              <InputTextarea id="adlParameterOptions" value={chartingParameterDraft.optionsText} onChange={(event) => setChartingParameterDraft((current: any) => ({ ...current, optionsText: event.target.value }))} rows={3} autoResize className="w-full" placeholder="Enter options separated by commas: 0%, 25%, 50%, 100%" />
            </div>
          ) : null}
        </div>
      </Dialog>
      <Dialog
        visible={showCustomTaskDialog}
        onHide={resetCustomTaskDialog}
        header={editingTaskId ? "Edit ADL Item" : "Add ADL Item"}
        style={{ width: "46rem", maxWidth: "96vw" }}
        footer={(
          <div className="flex justify-content-between align-items-center gap-2 flex-wrap">
            <div className="flex gap-2 flex-wrap">
              {editingTaskId ? (
                <>
                  <Button
                    type="button"
                    label={draftTasks.find((task) => task.taskId === editingTaskId)?.enabled === false ? "Make Active" : "Delete"}
                    rounded
                    outlined
                    severity={draftTasks.find((task) => task.taskId === editingTaskId)?.enabled === false ? "success" : "danger"}
                    onClick={handleDeleteEditingTask}
                  />
                </>
              ) : <span />}
            </div>
            <div className="flex justify-content-end gap-2">
              <Button type="button" label="Cancel" rounded outlined severity="secondary" onClick={resetCustomTaskDialog} />
              <Button type="button" label={editingTaskId ? "Save Changes" : "Add Task"} rounded severity="warning" onClick={handleAddCustomTask} />
            </div>
          </div>
        )}
      >
        <div className="grid">
          <div className="col-12 md:col-6" style={{ minWidth: 0 }}>
            <label htmlFor="adlTaskLabel" className="block mb-2 font-semibold">Task</label>
            <AutoComplete
              id="adlTaskLabel"
              value={customTask.label}
              suggestions={adlItemSuggestions}
              completeMethod={searchAdlItemSuggestions}
              onDropdownClick={(event) => searchAdlItemSuggestions(event)}
              minLength={0}
              field="label"
              dropdown
              placeholder="Pick or Write New Task"
              className="w-full"
              inputClassName="w-full"
              onChange={(event) => setCustomTask((current: any) => ({ ...current, label: typeof event.value === "string" ? event.value : String(event.value?.label || "") }))}
              onSelect={(event) => applyAdlItemExample(event.value)}
            />
          </div>
          <div className="col-12 md:col-6" style={{ minWidth: 0 }}>
            <label htmlFor="adlTaskSupport" className="block mb-2 font-semibold">Support Level</label>
            <Dropdown id="adlTaskSupport" value={customTask.supportLevel} options={supportLevelOptions} onChange={(event) => setCustomTask((current: any) => ({ ...current, supportLevel: event.value }))} className="w-full" />
          </div>
          {Array.isArray(customTask?.chartingParameters) && customTask.chartingParameters.length ? (
            <div className="col-12" style={{ minWidth: 0 }}>
              <div style={{ border: "1px solid #e2e8f0", borderRadius: "12px", padding: "0.85rem", background: "#f8fafc" }}>
                <div className="flex justify-content-between align-items-center gap-2 flex-wrap">
                  <div style={{ fontWeight: 700, color: "#0f172a" }}>Charting parameters</div>
                  <Button type="button" label="Add parameter" icon="pi pi-plus" rounded outlined severity="warning" onClick={() => openChartingParameterDialog()} />
                </div>
                <div className="mt-2 flex flex-column gap-2">
                  {customTask.chartingParameters.map((parameter: any) => (
                    <div key={parameter.parameterId} className="flex justify-content-between align-items-center gap-2" style={{ border: "1px solid #cbd5e1", borderRadius: "10px", padding: "0.55rem 0.7rem", background: "#fff" }}>
                      <div style={{ color: "#334155", fontWeight: 700 }}>{parameter.label} <span style={{ fontWeight: 500 }}>({parameter.type === "number" ? "Enter a number" : (parameter.options || []).join(", ")})</span></div>
                      <div className="flex gap-1">
                        <Button type="button" icon="pi pi-pencil" text rounded severity="warning" aria-label={`Edit ${parameter.label}`} onClick={() => openChartingParameterDialog(parameter)} />
                        <Button type="button" icon="pi pi-times" text rounded severity="secondary" aria-label={`Remove ${parameter.label}`} onClick={() => removeChartingParameter(String(parameter.parameterId || ""))} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="col-12">
              <Button type="button" label="Add charting parameter" icon="pi pi-plus" rounded outlined severity="warning" onClick={() => openChartingParameterDialog()} />
            </div>
          )}
          <div className="col-12 md:col-6" style={{ minWidth: 0 }}>
            <label htmlFor="adlTaskInstructions" className="block mb-2 font-semibold">Instructions</label>
            <InputTextarea id="adlTaskInstructions" value={customTask.instructions} onChange={(event) => setCustomTask((current: any) => ({ ...current, instructions: event.target.value }))} rows={3} autoResize className="w-full" />
          </div>
          <div className="col-12 md:col-6" style={{ minWidth: 0 }}>
            <label htmlFor="adlTaskPrecautions" className="block mb-2 font-semibold">Precautions</label>
            <InputTextarea id="adlTaskPrecautions" value={customTask.precautions} onChange={(event) => setCustomTask((current: any) => ({ ...current, precautions: event.target.value }))} rows={3} autoResize className="w-full" />
          </div>
          <div className="col-12 md:col-6" style={{ minWidth: 0 }}>
            <label htmlFor="adlTaskFrequency" className="block mb-2 font-semibold">Frequency</label>
            <Dropdown
              id="adlTaskFrequency"
              value={inferAdlFrequencyPreset(customTask)}
              options={adlFrequencyOptions}
              optionLabel="label"
              optionValue="value"
              placeholder="Select frequency"
              onChange={(event) => {
                const frequencyPreset = String(event.value || "custom");
                const currentTimes = parseAdlScheduleTimes(customTask?.scheduledTimesText);
                const nextTimes = defaultAdlScheduleTimes[frequencyPreset] || currentTimes;
                setCustomTask((current: any) => ({
                  ...current,
                  frequencyPreset,
                  frequencyType: "scheduled_times",
                  timesPerDay: Math.max(1, nextTimes.length),
                  scheduledTimesText: nextTimes.join(", "),
                  scheduleDayPattern: frequencyPreset === "weekly"
                    ? "specific_days"
                    : current?.scheduleDayPattern || "daily",
                  scheduledDays: frequencyPreset === "weekly" && !(Array.isArray(current?.scheduledDays) && current.scheduledDays.length)
                    ? ["mon"]
                    : current?.scheduledDays || [],
                  scheduleMonthDays: current?.scheduleMonthDays || [],
                }));
              }}
              className="w-full"
            />
          </div>
          <div className="col-12 md:col-6" style={{ minWidth: 0 }}>
            <label htmlFor="adlTaskScheduleDays" className="block mb-2 font-semibold">Schedule days</label>
            <Dropdown
              id="adlTaskScheduleDays"
              value={normalizeAdlScheduleDayPattern(customTask?.scheduleDayPattern)
                || (Array.isArray(customTask.scheduleMonthDays) && customTask.scheduleMonthDays.length ? "specific_month_days" : "")
                || (Array.isArray(customTask.scheduledDays) && customTask.scheduledDays.length ? "specific_days" : "daily")}
              options={adlScheduleDayPatternOptions}
              optionLabel="label"
              optionValue="value"
              onChange={(event) => setCustomTask((current: any) => ({
                ...current,
                scheduleDayPattern: String(event.value || "daily"),
                scheduledDays: event.value === "specific_days"
                  ? (Array.isArray(current?.scheduledDays) && current.scheduledDays.length ? current.scheduledDays : ["mon"])
                  : current?.scheduledDays || [],
                scheduleMonthDays: event.value === "specific_month_days"
                  ? (Array.isArray(current?.scheduleMonthDays) && current.scheduleMonthDays.length ? current.scheduleMonthDays : [1])
                  : current?.scheduleMonthDays || [],
              }))}
              className="w-full"
            />
            {String(customTask?.scheduleDayPattern || "daily") === "specific_days" ? (
              <MultiSelect
                value={normalizeAdlScheduleDays(customTask.scheduledDays)}
                options={adlWeekdayOptions}
                optionLabel="label"
                optionValue="value"
                onChange={(event) => setCustomTask((current: any) => ({ ...current, scheduledDays: normalizeAdlScheduleDays(event.value) }))}
                display="chip"
                placeholder="Select weekdays"
                selectedItemsLabel="{0} days selected"
                showSelectAll={false}
                className="w-full mt-2"
              />
            ) : null}
            {String(customTask?.scheduleDayPattern || "daily") === "specific_month_days" ? (
              <MultiSelect
                value={normalizeAdlScheduleMonthDays(customTask.scheduleMonthDays)}
                options={adlScheduleMonthDayOptions}
                optionLabel="label"
                optionValue="value"
                onChange={(event) => setCustomTask((current: any) => ({ ...current, scheduleMonthDays: normalizeAdlScheduleMonthDays(event.value) }))}
                display="chip"
                placeholder="Select month dates"
                selectedItemsLabel="{0} dates selected"
                showSelectAll={false}
                className="w-full mt-2"
              />
            ) : null}
            <div className="mt-2" style={{ ...appMutedTextStyle, fontSize: "0.84rem" }}>
              {String(customTask?.scheduleDayPattern || "daily") === "specific_days"
                ? "Choose the weekdays this ADL item should appear on the workflow."
                : String(customTask?.scheduleDayPattern || "daily") === "specific_month_days"
                  ? "Choose the month dates this ADL item should appear on the workflow."
                  : String(customTask?.scheduleDayPattern || "daily") === "alternate_days"
                    ? "Alternating days are counted from the effective start date when available."
                    : "Use this when the item is not scheduled every day."}
            </div>
          </div>
          <div className="col-12" style={{ minWidth: 0 }}>
            <label htmlFor="adlTaskSchedule" className="block mb-2 font-semibold">Schedule</label>
            <MultiSelect
              id="adlTaskSchedule"
              value={parseAdlScheduleTimes(customTask.scheduledTimesText)}
              options={adlScheduleTimeOptions}
              optionLabel="label"
              optionValue="value"
              display="chip"
              placeholder="Select times"
              maxSelectedLabels={4}
              selectedItemsLabel="{0} times selected"
              showSelectAll={false}
              onChange={(event) => {
                const nextTimes = Array.isArray(event.value) ? [...event.value].sort() : [];
                setCustomTask((current: any) => ({
                  ...current,
                  frequencyPreset: "custom",
                  frequencyType: nextTimes.length ? "scheduled_times" : "daily",
                  timesPerDay: Math.max(1, nextTimes.length),
                  scheduledTimesText: nextTimes.join(", "),
                }));
              }}
              className="w-full"
            />
          </div>
          <div className="col-12 md:col-6" style={{ minWidth: 0 }}>
            <label className="block mb-2 font-semibold">Start date</label>
            <div className="flex gap-2 flex-wrap align-items-end">
              <Calendar
                value={getLosAngelesDateOnlyCalendarValue(customTask?.effectiveStartDate)}
                onChange={(event) => {
                  const nextStartDate = formatLosAngelesDateOnlyFromCalendar(event.value as Date | null);
                  setCustomTask((current: any) => ({ ...current, effectiveStartDate: nextStartDate }));
                }}
                dateFormat="mm/dd/yy"
                placeholder="Select start date"
                showButtonBar
                monthNavigator
                yearNavigator
                yearRange="2020:2035"
                style={{ width: "100%", maxWidth: "14rem" }}
              />
              <Button
                type="button"
                icon="pi pi-times"
                outlined
                rounded
                aria-label="Clear start date"
                onClick={() => setCustomTask((current: any) => ({ ...current, effectiveStartDate: "" }))}
              />
            </div>
          </div>
          <div className="col-12 md:col-6" style={{ minWidth: 0 }}>
            <label className="block mb-2 font-semibold">Expiration date</label>
            <div className="flex gap-2 flex-wrap align-items-end">
              <Calendar
                value={getLosAngelesDateOnlyCalendarValue(customTask?.effectiveEndDate)}
                onChange={(event) => {
                  const nextEndDate = formatLosAngelesDateOnlyFromCalendar(event.value as Date | null);
                  setCustomTask((current: any) => ({ ...current, effectiveEndDate: nextEndDate }));
                }}
                dateFormat="mm/dd/yy"
                placeholder="Select expiration date"
                showButtonBar
                monthNavigator
                yearNavigator
                yearRange="2020:2035"
                style={{ width: "100%", maxWidth: "14rem" }}
              />
              <Button
                type="button"
                icon="pi pi-times"
                outlined
                rounded
                aria-label="Clear expiration date"
                onClick={() => setCustomTask((current: any) => ({ ...current, effectiveEndDate: "" }))}
              />
            </div>
          </div>
          <div className="col-12 md:col-6" style={{ minWidth: 0 }}>
            <label className="mt-3 flex align-items-center gap-2" htmlFor="adlTaskDiscontinuedToggle" style={{ cursor: "pointer", width: "fit-content" }}>
              <Checkbox
                inputId="adlTaskDiscontinuedToggle"
                checked={!!customTask?.discontinued}
                onChange={(event) => {
                  const nextChecked = !!event.checked;
                  setCustomTask((current: any) => ({
                    ...current,
                    discontinued: nextChecked,
                    discontinuedAt: nextChecked
                      ? (current?.discontinuedAt || getLosAngelesTodayDateString())
                      : "",
                  }));
                }}
              />
              <span style={{ fontWeight: 600, color: "#0f172a" }}>Discontinued</span>
            </label>
            <div className="mt-2" style={{ ...appMutedTextStyle, fontSize: "0.84rem" }}>
              Mark this item discontinued so it stays visible in history, but no longer behaves as active.
            </div>
            {customTask?.discontinued ? (
              <div className="mt-2">
                <label className="block mb-2 font-semibold">Discontinued on</label>
                <div className="flex gap-2 flex-wrap align-items-end">
                  <Calendar
                    value={getLosAngelesDateOnlyCalendarValue(customTask?.discontinuedAt)}
                    onChange={(event) => {
                      const nextDiscontinuedDate = formatLosAngelesDateOnlyFromCalendar(event.value as Date | null);
                      setCustomTask((current: any) => ({ ...current, discontinuedAt: nextDiscontinuedDate }));
                    }}
                    dateFormat="mm/dd/yy"
                    placeholder="Select discontinued date"
                    showButtonBar
                    monthNavigator
                    yearNavigator
                    yearRange="2020:2035"
                    style={{ width: "100%", maxWidth: "14rem" }}
                  />
                  <Button
                    type="button"
                    icon="pi pi-times"
                    outlined
                    rounded
                    aria-label="Clear discontinued date"
                    onClick={() => setCustomTask((current: any) => ({ ...current, discontinuedAt: "" }))}
                  />
                </div>
                <label className="block mt-3 mb-2 font-semibold" htmlFor="adlTaskDiscontinuedReason">Reason</label>
                <InputTextarea
                  id="adlTaskDiscontinuedReason"
                  value={String(customTask?.discontinuedReason || "")}
                  onChange={(event) => setCustomTask((current: any) => ({ ...current, discontinuedReason: event.target.value }))}
                  rows={2}
                  maxLength={500}
                  autoResize
                  placeholder="Why is this item discontinued?"
                  className="w-full"
                />
              </div>
            ) : null}
          </div>
        </div>
      </Dialog>
      <Dialog
        visible={showMissingCarePlanDialog}
        onHide={() => setShowMissingCarePlanDialog(false)}
        header="Manage Care Plan First"
        style={{ width: mobile ? "96vw" : "34rem" }}
        footer={(
          <div className="flex justify-content-end gap-2 flex-wrap">
            <Button
              type="button"
              label="Close"
              rounded
              outlined
              severity="secondary"
              onClick={() => setShowMissingCarePlanDialog(false)}
            />
            {resolvedHomeId && residentId ? (
              <Button
                type="button"
                label="Go to Manage Care Plan"
                rounded
                severity="warning"
                onClick={() => {
                  setShowMissingCarePlanDialog(false);
                  navigate(RouterAddress.getResidentNCPPagePath(resolvedHomeId, residentId));
                }}
              />
            ) : null}
          </div>
        )}
      >
        <div style={{ color: "#475569", lineHeight: 1.7 }}>
          Create a care plan for this resident in Manage Care Plan before generating ADL tasks.
        </div>
      </Dialog>
      <Dialog
        visible={showGenerateTasksDialog}
        onHide={() => {
          if (!processingGeneration && !saving) {
            setShowGenerateTasksDialog(false);
          }
        }}
        header="Generate ADL Tasks from Care Plan"
        style={{ width: "34rem", maxWidth: "96vw" }}
        footer={(
          <div className="flex flex-wrap justify-content-between align-items-center gap-3">
            <p style={{ margin: 0, fontSize: "0.82rem", color: "#64748b", fontStyle: "italic" }}>
              {AI_MESSAGE_FOR_COMPLIANCE}
            </p>
            <div className="flex justify-content-end gap-2">
              <Button
                type="button"
                label="Close"
                rounded
                outlined
                severity="secondary"
                disabled={processingGeneration || saving}
                onClick={() => setShowGenerateTasksDialog(false)}
              />
              {!generationCompleted ? (
                <Button
                  type="button"
                  label={processingGeneration ? "Generating..." : "Start Generation"}
                  rounded
                  severity="warning"
                  loading={processingGeneration || saving}
                  onClick={() => { void handleGenerateFromCarePlan(); }}
                />
              ) : null}
            </div>
          </div>
        )}
      >
        {generationIsRunning || processingGeneration ? (
          <div className="col-12 text-center d-flex flex-column justify-content-center align-items-center">
            <ProgressSpinner role="status" style={{ color: "#185DA2" }} />
            <div className="block mt-3" style={{ color: "#0f172a", fontWeight: 700 }}>
              Generating ADL tasks...
            </div>
            <div className="block mt-2" style={{ color: "#596F80" }}>
              <strong>Job Status:</strong> {generationJobStatus || "Starting"}
            </div>
            <div className="block mt-2" style={{ color: "#596F80" }}>
              <strong>Elapsed time:</strong> {formatElapsedTime(generationElapsedSeconds)}
            </div>
          </div>
        ) : (
          <div style={{ display: "grid", gap: "1rem" }}>
            <div style={{ ...appInnerCardStyle, padding: "1rem" }}>
              <div className="flex align-items-start justify-content-between gap-2 flex-wrap">
                <div style={{ fontWeight: 800, color: "#0f172a" }}>
                  {generationCompleted ? "Task Summary" : "Generation Status"}
                </div>
                <div className="flex gap-2 flex-wrap">
                  {latestGenerationTag}
                </div>
              </div>
              <div style={{ ...appMutedTextStyle, marginTop: "0.6rem", lineHeight: 1.6 }}>
                {generationStatusDetail}
              </div>
              {lastGenerationAt ? (
                <div style={{ ...appMutedTextStyle, marginTop: "0.75rem", fontSize: "0.82rem" }}>
                  Last update: {formatLosAngelesDateTime(lastGenerationAt) || lastGenerationAt}
                </div>
              ) : null}
            </div>
            <div style={{ ...appMutedTextStyle, lineHeight: 1.7 }}>
              We use the resident care plan to build a working ADL setup. Review the tasks, edit anything you need, and then make the setup live for caregivers.
            </div>
          </div>
        )}
      </Dialog>
      <Dialog
        visible={showReportDialog}
        style={{ width: "32rem", maxWidth: "96vw" }}
        modal
        onHide={() => {
          if (!exportingReport) {
            setShowReportDialog(false);
          }
        }}
        header={renderSurfaceHeader(
          "Generate ADL Report",
          "Choose a month, the last 7 days, or a custom range of up to 31 days.",
          "pi pi-calendar",
        )}
      >
        <div className="pt-2 flex flex-column gap-3">
          <div style={{ ...appNeutralPanelStyle, padding: "1rem" }}>
            <div className="flex align-items-center gap-2 flex-wrap mb-3">
              <Button
                type="button"
                label="Monthly"
                outlined={reportRangeMode !== "monthly"}
                rounded
                severity={reportRangeMode === "monthly" ? "success" : "warning"}
                onClick={() => setReportRangeMode("monthly")}
              />
              <Button
                type="button"
                label="Custom range"
                outlined={reportRangeMode !== "custom"}
                rounded
                severity={reportRangeMode === "custom" ? "success" : "warning"}
                onClick={() => setReportRangeMode("custom")}
              />
              <Button
                type="button"
                label="Last 7 days"
                outlined={reportRangeMode !== "last7Days"}
                rounded
                severity={reportRangeMode === "last7Days" ? "success" : "warning"}
                onClick={() => setReportRangeMode("last7Days")}
              />
            </div>
            {reportRangeMode === "monthly" ? (
              <>
                <div style={{ fontWeight: 700, color: "#0f172a", marginBottom: "0.5rem" }}>Report month</div>
                <Dropdown
                  id="adlReportMonth"
                  value={selectedReportMonth}
                  options={reportMonthOptions}
                  onChange={(event) => setSelectedReportMonth(String(event.value || ""))}
                  className="w-full"
                />
                <div className="mt-2" style={{ ...appMutedTextStyle, fontSize: "0.84rem" }}>
                  The monthly report generates the full selected month.
                </div>
              </>
            ) : reportRangeMode === "custom" ? (
              <div className="grid">
                <div className="col-12">
                  <label className="block mb-2 font-semibold">Start date</label>
                  <Calendar value={getLosAngelesDateOnlyCalendarValue(reportStartDate)} onChange={(event) => setReportStartDate(formatLosAngelesDateOnlyFromCalendar(event.value as Date | null))} dateFormat="mm/dd/yy" showIcon readOnlyInput className="w-full" />
                </div>
                <div className="col-12">
                  <label className="block mb-2 font-semibold">End date</label>
                  <Calendar value={getLosAngelesDateOnlyCalendarValue(reportEndDate)} onChange={(event) => setReportEndDate(formatLosAngelesDateOnlyFromCalendar(event.value as Date | null))} dateFormat="mm/dd/yy" showIcon readOnlyInput className="w-full" />
                </div>
                <div className="col-12" style={{ ...appMutedTextStyle, fontSize: "0.84rem" }}>Custom reports support up to 31 calendar days.</div>
              </div>
            ) : (
              <div style={{ ...appMutedTextStyle, lineHeight: 1.6 }}>
                Includes today and the previous 6 days, based on the America/Los_Angeles calendar.
              </div>
            )}
            <div className="mt-3">
              <label style={{ display: "flex", alignItems: "center", gap: "0.7rem", cursor: "pointer" }}>
                <Checkbox
                  inputId="adl-blank-report-checkbox"
                  checked={blankAdlReport}
                  onChange={(event) => setBlankAdlReport(Boolean(event.checked))}
                />
                <span style={{ fontWeight: 700, color: "#0f172a" }}>Generate blank ADL report</span>
              </label>
              <div className="mt-2" style={{ ...appMutedTextStyle, fontSize: "0.84rem" }}>
                Keep tasks, schedules, resident details, and legends, but leave daily chart cells blank.
              </div>
            </div>
          </div>
          <div className="flex flex-wrap justify-content-end gap-2">
            <Button
              type="button"
              label="Cancel"
              severity="secondary"
              outlined
              disabled={exportingReport}
              onClick={() => setShowReportDialog(false)}
            />
            <Button
              type="button"
              label="Download"
              severity="info"
              outlined
              rounded
              loading={exportingReport}
              onClick={() => { void handleDownloadReport(); }}
            />
          </div>
        </div>
      </Dialog>
      <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
        {renderSurfaceHeader(
          `${adlData?.residentName || "Resident"} - ADL Tracking`,
          "Review the resident ADL list and chart today's pass.",
          "pi pi-check-square",
        )}

        {loading ? (
          <div className="flex justify-content-center p-5">
            <ProgressSpinner style={{ width: "48px", height: "48px" }} />
          </div>
        ) : (
          <>
            {!hasAnyAdlTasks ? (
              <div style={{ ...appInnerCardStyle, textAlign: "center", background: "#f8fafc" }}>
                <div style={{ fontSize: "1rem", fontWeight: 800, color: "#0f172a" }}>ADL List</div>
                <div className="mt-2" style={{ fontSize: "0.96rem", fontWeight: 700, color: "#0f172a" }}>
                  No ADL items yet
                </div>
                <div className="mt-2" style={{ ...appMutedTextStyle, maxWidth: "42rem", margin: "0 auto" }}>
                  Add an item manually, generate ADL tasks from the care plan, or review the care plan before building the list.
                </div>
                <div className="mt-3 flex justify-content-center gap-2 flex-wrap">
                  <Button
                    type="button"
                    label="Add Item"
                    icon="pi pi-plus"
                    severity="warning"
                    rounded
                    onClick={() => window.dispatchEvent(new CustomEvent("resident-adl:open-custom-dialog"))}
                  />
                  <Button
                    type="button"
                    label="Generate from Care Plan"
                    icon="pi pi-sparkles"
                    severity="warning"
                    rounded
                    outlined
                    disabled={processingGeneration || saving}
                    onClick={handleOpenGenerateTasksDialog}
                  />
                </div>
              </div>
            ) : null}
            {hasAnyAdlTasks ? (
              <TabView activeIndex={activeTabIndex} onTabChange={(event) => setActiveTabIndex(event.index)} className="resident-workspace-tabs">
                <TabPanel header={`Today's Pass (${visibleEntryDrafts.length})`}>
                  <div style={{ ...appInnerCardStyle, padding: "1rem 1.1rem" }}>
                    <div className="flex justify-content-between align-items-start gap-3 flex-wrap">
                      <div>
                        <div style={{ fontSize: "1rem", fontWeight: 800, color: "#0f172a" }}>Today&apos;s Pass</div>
                        <div className="mt-1" style={{ ...appMutedTextStyle, maxWidth: "40rem" }}>
                          Review today's ADL tasks and chart what happened for each one.
                        </div>
                      </div>
                      <div className="flex gap-2 flex-wrap align-items-center justify-content-end">
                        <Tag value={`${visibleEntryDrafts.length} total`} severity="info" />
                        <Tag value={`${todayChartedCount} charted`} severity={todayChartedCount > 0 ? "success" : "secondary"} />
                        <Tag value={`${todayPendingCount} pending`} severity={todayPendingCount > 0 ? "warning" : "success"} />
                      </div>
                    </div>
                    <div className="mt-3 flex gap-2 flex-wrap align-items-center justify-content-end">
                      <span style={{ ...appMutedTextStyle, fontWeight: 700 }}>Sort by</span>
                      <Button
                        type="button"
                        label="Task"
                        size="small"
                        rounded
                        severity={todayPassSortBy === "task" ? "warning" : "secondary"}
                        outlined={todayPassSortBy !== "task"}
                        onClick={() => setTodayPassSortBy("task")}
                      />
                      <Button
                        type="button"
                        label="Time"
                        size="small"
                        rounded
                        severity={todayPassSortBy === "time" ? "warning" : "secondary"}
                        outlined={todayPassSortBy !== "time"}
                        onClick={() => setTodayPassSortBy("time")}
                      />
                      <span style={{ ...appMutedTextStyle, fontWeight: 700 }}>Filter by</span>
                      <Dropdown
                        value={todayPassFilter}
                        options={adlPassFilterOptions}
                        onChange={(event) => setTodayPassFilter(event.value)}
                        style={{ minWidth: "10rem" }}
                      />
                    </div>
                    <div className="mt-3">
                      {hasPublishedTasks && visibleEntryDrafts.length > 0 ? (
                        <>
                          <div className="block md:hidden">
                            <div className="flex flex-column gap-3">
                              {sortedTodayEntryDrafts.map((row: any, index: number) => (
                                <div
                                  key={row?.taskId || `adl-entry-${index}`}
                                  style={{
                                    padding: "0.1rem 0",
                                    borderBottom: index === sortedTodayEntryDrafts.length - 1 ? "none" : "1px solid #e2e8f0",
                                  }}
                                >
                                  {passTaskBody(row)}
                                  <div className="mt-3">{todayChartSummaryBody(row)}</div>
                                  <div className="mt-3">{todayChartActionBody(row)}</div>
                                </div>
                              ))}
                            </div>
                          </div>
                          <div className="hidden md:block">
                            <DataTable value={sortedTodayEntryDrafts} responsiveLayout="scroll" size="small" stripedRows>
                              <Column header="Task" body={passTaskBody} style={{ width: "30%", maxWidth: "30%", minWidth: 0, whiteSpace: "normal", overflowWrap: "anywhere" }} />
                              <Column header="Status" body={todayChartSummaryBody} style={{ minWidth: "17rem" }} />
                              <Column header="Actions" body={todayChartActionBody} style={{ minWidth: "20rem" }} />
                            </DataTable>
                          </div>
                        </>
                      ) : (
                        <div style={{ ...appMutedTextStyle, textAlign: "center", padding: "1rem 0.5rem" }}>
                          No ADL tasks are currently showing in Today's Pass.
                        </div>
                      )}
                    </div>
                  </div>
                </TabPanel>
                <TabPanel header={`ADL List (${displayedDraftTasks.length})`}>
                  <div style={{ ...appInnerCardStyle, padding: "1rem 1.1rem" }}>
                    <div className="flex justify-content-between align-items-center gap-2 flex-wrap mt-3">
                      <div style={{ fontWeight: 800, color: "#0f172a" }}>
                        {adlListFilter === "inactive"
                          ? `Deleted items (${deletedDraftTasks.length})`
                          : adlListFilter === "discontinued"
                            ? `Discontinued items (${discontinuedDraftTasks.length})`
                            : `Active items (${activeDraftTasks.length})`}
                      </div>
                      <div className="flex align-items-center gap-2 flex-wrap">
                        <Button
                          type="button"
                          label="Task history"
                          icon="pi pi-history"
                          severity="info"
                          outlined
                          rounded
                          disabled={!Array.isArray(adlData?.draftTemplate?.taskHistory) || !adlData.draftTemplate.taskHistory.length}
                          onClick={() => setAdlTaskHistoryDialogVisible(true)}
                        />
                        <Button
                          type="button"
                          label={saving ? "Resetting..." : "Reset ADL List"}
                          icon={saving ? "pi pi-spin pi-spinner" : "pi pi-refresh"}
                          severity="danger"
                          outlined
                          rounded
                          disabled={saving || !hasResettableTasks}
                          onClick={handleResetDraftList}
                        />
                        <div className="flex align-items-center gap-2 flex-wrap">
                          <span
                            style={{
                              color: "#64748b",
                              fontWeight: 700,
                              fontSize: "0.85rem",
                              whiteSpace: "nowrap",
                            }}
                          >
                            Filter
                          </span>
                          <Dropdown
                            value={adlListFilter}
                            options={adlListFilterOptions}
                            onChange={(e) => setAdlListFilter(e.value)}
                            style={{ minWidth: "18rem" }}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="mt-3">
                      {displayedDraftTasks.length ? (
                        <>
                          <div className="block md:hidden">
                            <div className="flex flex-column gap-3">
                              {sortedDisplayedDraftTasks.map((row: any, index: number) => (
                                <div
                                  key={row?.taskId || `${row?.label || "adl-task"}-${index}`}
                                  style={{ ...appInnerCardStyle, padding: "0.9rem 1rem" }}
                                >
                                  {adlTaskBody(row)}
                                  <div className="mt-3">
                                    {draftActionsBody(row)}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                          <div className="hidden md:block">
                            <DataTable
                              value={sortedDisplayedDraftTasks}
                              responsiveLayout="scroll"
                              size="small"
                              stripedRows
                              emptyMessage={
                                adlListFilter === "inactive"
                                  ? "No deleted ADL items."
                                  : adlListFilter === "discontinued"
                                    ? "No discontinued ADL items."
                                    : "No active ADL items."
                              }
                            >
                              <Column header="Task" body={adlTaskBody} style={{ width: "30%", maxWidth: "30%", minWidth: 0, whiteSpace: "normal", overflowWrap: "anywhere" }} />
                              <Column header="Parameters" body={parametersBody} style={{ minWidth: "18rem" }} />
                              <Column field="supportLevel" header="Support Level" style={{ minWidth: "14rem" }} />
                              <Column header="Actions" body={draftActionsBody} style={{ minWidth: "12rem" }} />
                            </DataTable>
                          </div>
                        </>
                      ) : (
                        <div style={{ ...appMutedTextStyle, textAlign: "center", padding: "1rem 0.5rem" }}>
                          {adlListFilter === "inactive"
                            ? "No deleted ADL items."
                            : adlListFilter === "discontinued"
                              ? "No discontinued ADL items."
                              : "No active ADL items."}
                        </div>
                      )}
                    </div>
                  </div>
                </TabPanel>
                <TabPanel header={`Past Entries (${historyEntryDrafts.length})`}>
                  <div style={{ ...appInnerCardStyle, padding: "1rem 1.1rem" }}>
                    <div className="flex justify-content-between align-items-start gap-3 flex-wrap">
                      <div>
                        <div style={{ fontSize: "1rem", fontWeight: 800, color: "#0f172a" }}>Past Entries</div>
                        <div className="mt-1" style={{ ...appMutedTextStyle, maxWidth: "40rem" }}>
                          Review and correct ADL passes for a selected date. Past entries are available for the previous 3 months.
                        </div>
                      </div>
                      <div className="flex gap-2 flex-wrap align-items-end">
                        {loadingHistory ? <Tag value="Loading..." severity="info" rounded /> : null}
                        <div>
                          <label className="block mb-2" style={{ fontSize: "0.78rem", fontWeight: 700, color: "#475569" }}>
                            Filter
                          </label>
                          <Dropdown
                            value={historyStatusFilter}
                            options={[{ label: "All entries", value: "all" }, { label: "Missed", value: "missed" }]}
                            optionLabel="label"
                            optionValue="value"
                            onChange={(event) => setHistoryStatusFilter(event.value as "all" | "missed")}
                            style={{ minWidth: "12rem" }}
                          />
                        </div>
                        <div>
                          <label htmlFor="adlHistoryDate" className="block mb-2" style={{ fontSize: "0.78rem", fontWeight: 700, color: "#475569" }}>
                            Date
                          </label>
                          <Calendar
                            id="adlHistoryDate"
                            value={historyDateValue}
                            minDate={historyMinDateValue || undefined}
                            maxDate={historyMaxDateValue || undefined}
                            dateFormat="mm/dd/yy"
                            showIcon
                            readOnlyInput
                            onChange={(event) => {
                              const nextDateKey = formatLosAngelesDateOnlyFromCalendar(event.value as Date | null);
                              if (nextDateKey) {
                                setHistoryDateKey(nextDateKey);
                              }
                            }}
                            style={{ minWidth: "13rem" }}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="mt-3 flex gap-2 flex-wrap">
                      <Tag value={String(historyEntryDrafts.length) + " total"} severity="info" rounded />
                      <Tag value={String(historyChartedCount) + " charted"} severity={historyChartedCount ? "success" : "secondary"} rounded />
                      <Tag value={String(historyPendingCount) + " not charted"} severity={historyPendingCount ? "warning" : "secondary"} rounded />
                    </div>
                    <div className="mt-3">
                      {filteredHistoryEntryDrafts.length ? (
                        <DataTable value={filteredHistoryEntryDrafts} responsiveLayout="scroll" size="small" stripedRows>
                          <Column header="Task" body={historyTaskBody} style={{ width: "30%", maxWidth: "30%", minWidth: 0, whiteSpace: "normal", overflowWrap: "anywhere" }} />
                          <Column header="Status" body={todayChartSummaryBody} style={{ minWidth: "17rem" }} />
                          <Column header="Actions" body={historyChartActionBody} style={{ minWidth: "20rem" }} />
                        </DataTable>
                      ) : (
                        <div style={{ ...appMutedTextStyle, textAlign: "center", padding: "1rem 0.5rem" }}>
                          No ADL passes are scheduled for this date.
                        </div>
                      )}
                    </div>
                  </div>
                </TabPanel>
              </TabView>
            ) : null}
            <Dialog
              visible={!!adlEntryToDelete}
              onHide={() => {
                if (!deletingAdlEntry) {
                  setAdlEntryToDelete(null);
                }
              }}
              header="Delete ADL Past Entry"
              modal
              closable={!deletingAdlEntry}
              style={{ width: "min(92vw, 32rem)" }}
              footer={(
                <div className="flex justify-content-end gap-2">
                  <Button
                    type="button"
                    label="Cancel"
                    severity="secondary"
                    outlined
                    rounded
                    disabled={deletingAdlEntry}
                    onClick={() => setAdlEntryToDelete(null)}
                  />
                  <Button
                    type="button"
                    label="Mark Deleted"
                    severity="danger"
                    rounded
                    loading={deletingAdlEntry}
                    onClick={() => { void markAdlEntryDeleted(); }}
                  />
                </div>
              )}
            >
              <div className="flex flex-column gap-2">
                <div style={{ fontWeight: 700, color: "#0f172a" }}>
                  {String(adlEntryToDelete?.label || "ADL entry").trim()}
                </div>
                <div style={{ color: "#334155", lineHeight: 1.5 }}>
                  Report will exclude this entry. Are you sure you want to mark it as deleted?
                </div>
              </div>
            </Dialog>

            <Dialog
              visible={adlHistoryDialogVisible}
              onHide={() => setAdlHistoryDialogVisible(false)}
              header={selectedAdlHistoryEntry?.label ? `ADL History: ${selectedAdlHistoryEntry.label}` : "ADL History"}
              modal
              style={{ width: "min(94vw, 46rem)" }}
            >
              <div className="flex flex-column gap-3">
                <div style={{ ...appMutedTextStyle }}>
                  Scheduled pass: {formatAdlOccurrenceLabel(selectedAdlHistoryEntry || {})} · Date: {selectedAdlHistoryEntry?.dateKey || historyDateKey}
                </div>
                {(Array.isArray(selectedAdlHistoryEntry?.statusHistory) ? selectedAdlHistoryEntry.statusHistory : []).slice().reverse().map((historyEvent: any, index: number) => (
                  <div key={String(historyEvent?.eventId || `adl-history-${index}`)} style={{ border: "1px solid #dbe4f0", borderRadius: "12px", padding: "0.85rem", background: "#fff" }}>
                    <div className="flex justify-content-between align-items-start gap-2 flex-wrap">
                      <Tag value={String(historyEvent?.eventType || "updated").replace(/_/g, " ")} severity="info" rounded />
                      <span style={{ ...appMutedTextStyle, fontSize: "0.82rem" }}>
                        {formatLosAngelesDateTime(historyEvent?.changedAt) || historyEvent?.changedAt || "Unknown time"}
                      </span>
                    </div>
                    <div className="mt-2" style={{ fontSize: "0.88rem", color: "#334155" }}>
                      <strong>Previous:</strong> {historyEvent?.previousValue || "None"} {historyEvent?.previousStatus ? `(${historyEvent.previousStatus})` : ""}
                    </div>
                    <div className="mt-1" style={{ fontSize: "0.88rem", color: "#334155" }}>
                      <strong>Updated:</strong> {historyEvent?.value || "None"} {historyEvent?.status ? `(${historyEvent.status})` : ""}
                    </div>
                    {historyEvent?.correctionReason ? (
                      <div className="mt-2" style={{ fontSize: "0.88rem", color: "#92400e" }}>
                        <strong>Reason:</strong> {historyEvent.correctionReason}
                      </div>
                    ) : null}
                    <div className="mt-2" style={{ ...appMutedTextStyle, fontSize: "0.82rem" }}>
                      Changed by: {historyEvent?.changedBy || "Unknown user"}
                    </div>
                  </div>
                ))}
              </div>
            </Dialog>
          </>
        )}
      </div>
    </div>
  );
};

export default ProviderResidentAdlPage;
